// 🔥 CLARA SOURCE OF TRUTH
// FILE: ClaraKIPage.jsx
// STATUS: ACTIVE PRODUCTION FILE
// LAST VERIFIED: 2026-04-17
// WARNING: DO NOT DUPLICATE THIS FILE
// ARCHIVE: /var/www/clara360/_archive/clara-ki/
// BUILD GUARD: This is the only authoritative version
//
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bot, Send, Mic, MicOff, Sparkles, Brain, Database, Trash2 } from 'lucide-react';
import LLMToggle from '../components/LLMToggle';
import FloatingUploadButton3D from '../components/FloatingUploadButton3D';
import UploadDocumentModal from '../components/UploadDocumentModal';
import CommandCenter from '../components/CommandCenter';
import { ACTIONS } from '@/config/actionRegistry';

// UNIFIED SUPABASE CLIENT - masterSupabase for everything
import { masterSupabase } from '../lib/supabase-master.js';

// Auth Context for user management
import { useAuth } from '../contexts/AuthContext';

// Working Voice Recognition Hook
import { useVoiceRecognition } from '../hooks/useVoiceRecognition';
import { useConversationPersistence } from '../hooks/useConversationPersistence';
import { getSupabaseContext } from "../services/context";
import PropertySelection from '../components/PropertySelection';
import { useProperties } from '../hooks/useProperties';
import { useUnits } from '../hooks/useUnits';
import TTSService from '../services/TTSService';
import { formatForVoice, detectResponseMode } from '../services/VoiceFormatter';

// 🔥 NORMALIZE MESSAGE - Single Source of Truth für alle Message-Typen
const normalizeMessage = (data) => {
  const messageType = data.type?.toLowerCase?.() || data.type || 'text';

  // 🔥 WORKFLOW FORM NORMALISIERUNG
  if (messageType === 'workflow_form') {
    return {
      id: Date.now().toString(),
      type: messageType,
      content: data.description || data.title || 'Formular ausfüllen',
      timestamp: new Date().toISOString(),
      isLoading: false,
      
      // 🔥 ROOT LEVEL FIELDS - mit Fallback auf workflow_form.fields (neuer Contract) oder data.fields
      fields: data.workflow_form?.fields || data.fields || data.workflow?.fields || [],
      title: data.title,
      description: data.description,
      
      // 🔥 HIER IST DER FIX: fields aus root-level ODER workflow.fields
      workflow: {
        name: data.workflow?.type || data.workflow?.name,
        fields: data.workflow_form?.fields || data.fields || data.workflow?.fields || [],  // ← Fallback-Chain
        description: data.description,
        title: data.title,
        actions: data.actions ?? [],
        data: data.data ?? data.workflow?.data ?? {},
        id: data.meta?.workflowId || data.workflow?.id || null  // 🔥 FIX: workflowId aus meta
      },
      
      action: data?.action,
      conversationId: data?.conversationId,
      requiresFormInput: true,
      error: data?.error
    };
  }

  // CLARA-COMMAND-CENTER-TRUTH-1: Kanonischer Capability-Input-Vertrag
  if (data.status === 'requires_input') {
    const schema = data.input_schema || {};
    return {
      id: Date.now().toString(),
      type: 'workflow_form',
      content: data.message || schema.description || 'Bitte ergänzen Sie die erforderlichen Angaben.',
      timestamp: new Date().toISOString(),
      isLoading: false,
      fields: schema.fields || [],
      title: schema.title || 'Angaben ergänzen',
      description: schema.description || data.message || '',
      workflow: {
        id: `capability-${data.data?.capability || data.tool || Date.now()}`,
        capability: data.data?.capability || null,
        name: data.data?.capability || data.tool || null,
        fields: schema.fields || [],
        title: schema.title || 'Angaben ergänzen',
        description: schema.description || data.message || '',
        data: data.data?.args || {}
      }
    };
  }

  if (data.status === 'requires_approval') {
    return {
      id: Date.now().toString(),
      type: 'action_confirmation',
      content: data.message || 'Freigabe erforderlich.',
      timestamp: new Date().toISOString(),
      isLoading: false,
      action: 'capability_execute',
      approvalId: data.approval_id || null,
      proposalHash: data.proposal_hash || null,
      capability: data.data?.capability || data.tool || null,
      requiresConfirmation: true
    };
  }

  // ACTION CONFIRMATION NORMALISIERUNG
  if (messageType === 'action_confirmation') {
    return {
      id: Date.now().toString(),
      type: messageType,
      content: data.message || data.description || '',
      timestamp: new Date().toISOString(),
      isLoading: false,
      action: data?.action,
      conversationId: data?.conversationId,
      requiresConfirmation: true,
      error: data?.error
    };
  }

  // WORKFLOW SUMMARY NORMALISIERUNG
  if (messageType === 'workflow_summary') {
    return {
      id: Date.now().toString(),
      type: messageType,
      content: data.description || data.message || '',
      timestamp: new Date().toISOString(),
      isLoading: false,
      workflow: data?.workflow || {},
      conversationId: data?.conversationId,
      error: data?.error
    };
  }

  // DEFAULT: Text/Assistant Message
  return {
    id: Date.now().toString(),
    type: messageType,
      content: data.message || data.response || data.answer_markdown || data.content || '',
    timestamp: new Date().toISOString(),
    isLoading: false,
    sources: Array.isArray(data.sources) ? data.sources : []
  };
};

// Animated Wave Component (KITT-Style) - Slower and more subtle
const AnimatedWave = ({ isActive = false }) => (
  <div className="relative w-full h-1 bg-gray-700/30 rounded-full overflow-hidden">
    <div 
      className={`absolute inset-0 rounded-full transition-all duration-500 ${
        isActive ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        background: 'linear-gradient(90deg, transparent 0%, #3b82f6 20%, #6366f1 50%, #8b5cf6 80%, transparent 100%)',
        animation: isActive ? 'wave-flow 4s ease-in-out infinite' : 'none'
      }}
    />
    <style jsx>{`
      @keyframes wave-flow {
        0% { transform: translateX(-100%); opacity: 0.3; }
        50% { opacity: 0.8; }
        100% { transform: translateX(100%); opacity: 0.3; }
      }
    `}</style>
  </div>
);


const VaultArtifactModal = ({ artifactReference, onClose }) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const artifactId = String(artifactReference?.artifact_id || artifactReference?.artifact_reference || '').trim();
  const highlightQuery = artifactReference?.highlight_query || 'Miete';

  useEffect(() => {
    let cancelled = false;
    if (!artifactId) {
      setState({ loading: false, error: 'Keine gültige Dokumentreferenz vorhanden.', data: null });
      return undefined;
    }
    (async () => {
      try {
        const response = await fetch(`/api/vault/clara360/documents/${encodeURIComponent(artifactId)}/highlights?query=${encodeURIComponent(highlightQuery)}`);
        if (!response.ok) throw new Error(response.status === 404 ? 'Das Dokument ist nicht im freigegebenen Vault-Bereich verfügbar.' : 'Die Dokumentvorschau konnte nicht geladen werden.');
        const data = await response.json();
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (error) {
        if (!cancelled) setState({ loading: false, error: error.message, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [artifactId, highlightQuery]);

  const highlights = state.data?.highlights || [];
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-label="Dokumentvorschau">
      <div className="w-full max-w-4xl max-h-[88vh] overflow-hidden rounded-2xl border border-indigo-500/30 bg-[#090909] shadow-2xl shadow-indigo-950/50">
        <div className="flex items-start justify-between gap-4 border-b border-white/10 px-5 py-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-indigo-300">Vault-Dokument</div>
            <h2 className="mt-1 truncate text-lg font-semibold text-white">{state.data?.title || artifactReference?.title || 'Dokumentvorschau'}</h2>
            <p className="mt-1 text-xs text-gray-400">Suche: <span className="text-indigo-200">{highlightQuery}</span>{state.data?.document_type ? ` · ${state.data.document_type}` : ''}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg border border-white/10 px-3 py-1.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white">Schließen</button>
        </div>
        <div className="max-h-[68vh] overflow-y-auto p-5">
          {state.loading && <div className="py-10 text-center text-sm text-gray-400">Belegstelle wird geladen …</div>}
          {state.error && <div className="rounded-lg border border-red-500/30 bg-red-950/30 p-4 text-sm text-red-200">{state.error}</div>}
          {!state.loading && !state.error && highlights.length === 0 && <div className="rounded-lg border border-amber-500/30 bg-amber-950/20 p-4 text-sm text-amber-100">Für „{highlightQuery}“ wurde keine Textstelle gefunden. Das Dokument bleibt korrekt zugeordnet; bitte nutze einen präziseren Klauselbegriff.</div>}
          {!state.loading && !state.error && highlights.map((highlight, index) => (
            <article key={`${highlight.page}-${highlight.start}-${index}`} className="mb-4 rounded-xl border border-indigo-400/20 bg-indigo-950/20 p-4">
              <div className="mb-2 flex items-center justify-between text-xs"><span className="font-semibold text-indigo-300">Seite {highlight.page}</span><span className="text-gray-500">{highlight.reason === 'exact_match' ? 'Exakter Treffer' : 'Klausel-Treffer'}</span></div>
              <p className="whitespace-pre-wrap text-sm leading-6 text-gray-100">{highlight.quote}</p>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

const SupabaseDocumentModal = ({ documentSource, onClose }) => {
  const [state, setState] = useState({ loading: true, error: null, document: null });
  const documentId = String(documentSource?.document_id || '').trim();

  useEffect(() => {
    let cancelled = false;
    if (!documentId) {
      setState({ loading: false, error: 'Keine gültige Dokumentreferenz vorhanden.', document: null });
      return undefined;
    }
    (async () => {
      try {
        const { data: { session } } = await masterSupabase.auth.getSession();
        const accessToken = session?.access_token;
        if (!accessToken) throw new Error('Bitte erneut anmelden, um das Dokument zu öffnen.');
        const response = await fetch(`/api/clara/documents/${encodeURIComponent(documentId)}/portal-view`, {
          headers: { Authorization: `Bearer ${accessToken}`, 'x-supabase-access-token': accessToken },
        });
        if (!response.ok) throw new Error(response.status === 404 ? 'Das Dokument ist in deinem Organisations- und Objektscope nicht verfügbar.' : 'Die Dokumentvorschau konnte nicht geladen werden.');
        const payload = await response.json();
        if (!payload?.document?.viewer_url) throw new Error('Dokumentdatei ist nicht verfügbar.');
        if (!cancelled) setState({ loading: false, error: null, document: payload.document });
      } catch (error) {
        if (!cancelled) setState({ loading: false, error: error.message, document: null });
      }
    })();
    return () => { cancelled = true; };
  }, [documentId]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-label="Dokumentvorschau">
      <div className="w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-2xl border border-indigo-500/30 bg-[#090909] shadow-2xl shadow-indigo-950/50">
        <div className="flex items-start justify-between gap-4 border-b border-white/10 px-5 py-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-indigo-300">Clara360-Dokument</div>
            <h2 className="mt-1 truncate text-lg font-semibold text-white">{state.document?.filename || documentSource?.filename || 'Dokumentvorschau'}</h2>
            <p className="mt-1 text-xs text-gray-400">{state.document?.property_name || documentSource?.property_name || 'Berechtigter Objektbestand'}{state.document?.document_type ? ` · ${state.document.document_type}` : ''}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg border border-white/10 px-3 py-1.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white">Schließen</button>
        </div>
        <div className="h-[72vh] bg-black">
          {state.loading && <div className="py-10 text-center text-sm text-gray-400">Dokument wird aus dem berechtigten Clara360-Bestand geladen …</div>}
          {state.error && <div className="m-5 rounded-lg border border-red-500/30 bg-red-950/30 p-4 text-sm text-red-200">{state.error}</div>}
          {!state.loading && !state.error && <iframe src={state.document.viewer_url} title={state.document.filename} className="h-full w-full border-0" />}
        </div>
      </div>
    </div>
  );
};

// Chat Message Component with intelligent KITT wave and Speech Controls
// NEW: Function to format message content with color-coding

const ChatMessage = ({ message, isUser, isLastClaraMessage, onSpeak, onStopSpeaking, isSpeaking, onActionConfirm, onActionCancel, onWorkflowSubmit, onWorkflowConfirm, onPropertySelect, onOpenArtifact, properties = [], units = [], portfolioData }) => {

  // Nachricht kopieren (wie im System): 1 Klick = Text in Zwischenablage
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    const text = String(message.content || '');
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Kopieren fehlgeschlagen:', e);
    }
  };
  
  const workflowContext = useMemo(() => ({
    properties: properties ?? [],
    units: units ?? []
  }), [properties, units]);
  
  return (
  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-6`}>
    <div className="flex items-end space-x-3 max-w-xs lg:max-w-md">
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
          <Bot className="w-4 h-4 text-white" />
        </div>
      )}
      
      <div
        className={`px-4 py-3 rounded-2xl backdrop-blur-sm border relative ${
          isUser
            ? 'bg-gradient-to-r from-blue-600/80 to-blue-700/80 text-white border-blue-500/30 ml-auto'
            : 'bg-gradient-to-r from-gray-800/60 to-gray-900/60 text-gray-100 border-gray-600/30'
        }`}
      >
        <div className="clara-markdown text-sm leading-relaxed text-inherit">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <p className="mb-3 last:mb-0 whitespace-pre-wrap">{children}</p>,
              h1: ({ children }) => <h1 className="mb-3 text-lg font-semibold text-white">{children}</h1>,
              h2: ({ children }) => <h2 className="mb-2 text-base font-semibold text-white">{children}</h2>,
              h3: ({ children }) => <h3 className="mb-2 text-sm font-semibold text-blue-100">{children}</h3>,
              ul: ({ children }) => <ul className="mb-3 list-disc space-y-1 pl-5">{children}</ul>,
              ol: ({ children }) => <ol className="mb-3 list-decimal space-y-1 pl-5">{children}</ol>,
              table: ({ children }) => <div className="mb-3 overflow-x-auto rounded-lg border border-white/10"><table className="min-w-full text-left text-xs">{children}</table></div>,
              th: ({ children }) => <th className="border-b border-white/10 bg-white/5 px-2 py-1.5 font-semibold">{children}</th>,
              td: ({ children }) => <td className="border-b border-white/5 px-2 py-1.5 align-top">{children}</td>,
              code: ({ children }) => <code className="rounded bg-white/10 px-1 py-0.5 font-mono text-[0.85em]">{children}</code>
            }}
          >
            {String(message.content || '')}
          </ReactMarkdown>
        </div>
        {!isUser && message.routing?.provider && (
          <div className="mt-2 inline-flex items-center rounded border border-blue-500/30 bg-blue-500/10 px-2 py-1 text-[10px] text-blue-200">
            {message.routing.provider} · {message.routing.model}
          </div>
        )}
        <div className="text-xs opacity-70 mt-2 flex items-center justify-between">
          <span>
            {new Date(message.timestamp).toLocaleTimeString('de-DE', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
          <div className="flex items-center space-x-2">
            {/* Kopieren-Button für alle Nachrichten */}
            <button
              onClick={handleCopy}
              className="p-1 rounded-full hover:bg-blue-600/20 transition-colors"
              title={copied ? 'Kopiert!' : 'Nachricht kopieren'}
            >
              {copied ? '✅' : '📋'}
            </button>
            {/* Speech Controls für Clara-Nachrichten */}
            {!isUser && (
              <div className="flex items-center space-x-1">
                {!isSpeaking ? (
                  <button
                    onClick={() => onSpeak(message.content)}
                    className="p-1 rounded-full hover:bg-blue-600/20 transition-colors"
                    title="Vorlesen"
                  >
                    🔊
                  </button>
                ) : (
                  <button
                    onClick={onStopSpeaking}
                    className="p-1 rounded-full hover:bg-red-600/20 transition-colors"
                    title="Stoppen"
                  >
                    🔇
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Action Buttons for confirmation requests */}
        {!isUser && message.type === 'action_confirmation' && message.action && (
          <div className="mt-3 pt-3 border-t border-gray-600/30">
            <div className="text-xs text-gray-300 mb-2">
              Aktion: {getActionDescription(message.action)}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onActionConfirm(message)}
                className="flex-1 px-3 py-2 bg-green-600/80 hover:bg-green-600 text-white text-sm rounded-lg transition-colors flex items-center justify-center space-x-1"
              >
                <span>✓</span>
                <span>Ja, ausführen</span>
              </button>
              <button
                onClick={() => onActionCancel()}
                className="flex-1 px-3 py-2 bg-gray-600/80 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors flex items-center justify-center space-x-1"
              >
                <span>✗</span>
                <span>Abbrechen</span>
              </button>
            </div>
          </div>
        )}
        
        {/* Workflow Form for interactive data collection */}
        {!isUser && message.type === 'workflow_form' && (message.workflow || message.fields) && (
          <WorkflowForm 
            workflow={message.workflow}
            fields={message.fields}
            title={message.title}
            description={message.description}
            onSubmit={onWorkflowSubmit}
            onCancel={onActionCancel}
            error={message.error}
            context={workflowContext}
          />
        )}
        
        {/* Workflow Summary for confirmation */}
        {!isUser && message.type === 'workflow_summary' && message.workflow && (
          <WorkflowSummary 
            workflow={message.workflow}
            onConfirm={onWorkflowConfirm}
            onCancel={onActionCancel}
          />
        )}
        
        {/* Property Selection for BankReport */}
        {!isUser && message.type === 'property_selection' && message.properties && (
          <PropertySelection
            properties={message.properties}
            action={message.action}
            onPropertySelect={onPropertySelect}
          />
        )}
        
        {/* Vault Dokument-Quellen — artifact_reference */}
        {!isUser && message.sources && message.sources.length > 0 && (
          <div className="mt-3 pt-3 border-t border-indigo-500/20">
            <div className="text-xs text-indigo-300 mb-2 font-semibold flex items-center gap-1">
              <span>📎</span>
              <span>Quellen ({message.sources.length})</span>
            </div>
            <div className="flex flex-col gap-2">
              {message.sources.map((doc, i) => {
                const isSupabaseDocument = doc.source_kind === 'supabase_document';
                const isVaultArtifact = doc.source_kind === 'vault_artifact';
                const canOpenSource = isSupabaseDocument || isVaultArtifact;
                const sourceTitle = isSupabaseDocument
                  ? 'Dokument im Clara360-Portal öffnen'
                  : isVaultArtifact
                    ? 'Vault-Belegstelle öffnen'
                    : 'Capital-Dokumentmetadaten – kein freigegebener Volltextbeleg';
                return (
                <div
                  key={doc.document_id || doc.artifact_reference || i}
                  className={`flex items-start gap-2 p-2 rounded-lg bg-gray-800/60 border border-gray-700/40 transition-all ${canOpenSource ? 'hover:border-indigo-500/40 cursor-pointer' : 'cursor-default opacity-90'}`}
                  title={sourceTitle}
                  onClick={canOpenSource ? () => onOpenArtifact?.(isSupabaseDocument ? doc : { ...doc, artifact_id: doc.artifact_id || doc.artifact_reference, highlight_query: doc.highlight_query || 'Miete' }) : undefined}
                >
                  <span className="text-indigo-400 text-xs mt-0.5 shrink-0">📄</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white font-medium truncate">{doc.filename}</div>
                    {doc.property_name && <div className="text-xs text-gray-400 truncate">{doc.property_name}</div>}
                    {doc.excerpt && <div className="text-xs text-gray-500 mt-1 line-clamp-2">&ldquo;{doc.excerpt.substring(0, 120)}&rdquo;</div>}
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1">
                    {doc.source_kind === 'capital_metadata' && <span className="text-xs px-1.5 py-0.5 rounded bg-slate-700/70 text-slate-300">Metadaten</span>}
                    {doc.document_type && <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-900/50 text-indigo-300">{doc.document_type}</span>}
                    {doc.relevance_score && <span className="text-xs text-gray-500">{Math.round(doc.relevance_score * 100)}%</span>}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}
        {!isUser && message.artifact_reference && (
          <button
            type="button"
            onClick={() => onOpenArtifact?.(message.artifact_reference)}
            className="mt-3 flex w-full items-center justify-between rounded-xl border border-indigo-400/30 bg-indigo-950/30 px-3 py-2 text-left transition hover:border-indigo-300/60 hover:bg-indigo-950/50"
            title="Vertragsdokument mit Belegstelle öffnen"
          >
            <span className="text-sm font-medium text-indigo-100">📄 {message.artifact_reference.label || 'Vertragsdokument öffnen'}</span>
            <span className="text-xs text-indigo-300">Belegstelle anzeigen</span>
          </button>
        )}
        {/* BankReport Display - Phase 2: Simple text rendering (Phase 3 will add rich UI) */}
        {!isUser && message.type === 'bankreport' && message.metrics && (
          <div className="mt-3 pt-3 border-t border-blue-500/30">
            <div className="text-xs text-blue-300 mb-2 font-semibold">
              📊 Finanzkennzahlen: {message.property?.property_name}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-gray-400">Jahresmiete:</span> <span className="text-green-400 font-semibold">{message.metrics.yearlyRent} €</span></div>
              <div><span className="text-gray-400">Auslastung:</span> <span className="text-blue-400 font-semibold">{message.metrics.occupancyRate}%</span></div>
              <div><span className="text-gray-400">Bruttorendite:</span> <span className="text-yellow-400 font-semibold">{message.metrics.grossYield}%</span></div>
              <div><span className="text-gray-400">Ø Miete/m²:</span> <span className="text-purple-400 font-semibold">{message.metrics.avgRentPerSqm} €</span></div>
            </div>
        {/* Quick-Action Buttons - Phase 3C: Conversation State */}
        {!isUser && message.quick_actions && message.quick_actions.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-600/30">
            <div className="flex flex-col space-y-2">
              {message.quick_actions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => {
                    // Quick actions are deprecated - use workflow system instead
                    console.log('[QUICK_ACTION] Deprecated quick_action clicked:', action);
                  }}
                  className="w-full px-4 py-2 bg-gradient-to-r from-blue-600/80 to-indigo-600/80 hover:from-blue-600 hover:to-indigo-600 text-white text-sm rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 border border-blue-500/30"
                >
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

          </div>
        )}
        
        {/* Clara's KITT wave - ONLY for the last Clara message */}
        {!isUser && isLastClaraMessage && (
          <div className="absolute -bottom-1 left-4 right-4">
            <AnimatedWave isActive={true} />
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

// Helper function to get action descriptions
const getActionDescription = (action) => {
  const descriptions = {
    'create_tenant': 'Neuen Mieter anlegen',
    'create_unit': 'Neue Einheit anlegen',
    'update_tenant': 'Mieterdaten ändern',
    'create_property': 'Neue Immobilie anlegen'
  };
  return descriptions[action] || 'Aktion ausführen';
};

// Workflow Form Component - Chat-styled inline form
const WorkflowForm = ({ workflow, fields, title, description, onSubmit, onCancel, error, context }) => {
  // 🔥 Use flat fields if provided, otherwise fall back to workflow.fields
  // 🔥 FIX: Guard against empty overwrite — prefer non-empty fields
  const _rawFieldsList = fields || workflow?.fields || [];
  const fieldsList = _rawFieldsList.length > 0 ? _rawFieldsList : (fields || workflow?.fields || []);
  const [formData, setFormData] = useState(workflow?.data || {});
  const [cancelled, setCancelled] = useState(false);
  const [workflowTitle] = useState(title || workflow?.title || 'Formular');
  const [workflowDescription] = useState(description || workflow?.description || '');
  const [businessData, setBusinessData] = useState({ properties: [], units: [] });
  
  // Load business data for dropdowns
  useEffect(() => {
    const loadBusinessData = async () => {
      try {
        // This would typically come from the auth context or be passed as props
        // For now, we'll use a placeholder
        setBusinessData({ properties: [], units: [] });
      } catch (error) {
        console.error('Error loading business data:', error);
      }
    };
    loadBusinessData();
  }, []);
  
  const handleInputChange = (fieldName, value) => {
    if (!fieldName) {
      console.warn("⚠️ SKIPPED UPDATE - INVALID KEY");
      return;
    }
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const capability = workflow?.capability || workflow?.name;
    if (!capability) {
      console.error('[CapabilityForm] Keine Capability im Formularvertrag.');
      return;
    }
    const missingRequired = fieldsList.filter(field => field.required).find(field => {
      const key = field.name || field.id;
      return !formData?.[key];
    });
    if (missingRequired) {
      alert(`${missingRequired.label} ist erforderlich`);
      return;
    }
    onSubmit?.({ capability, args: formData }, workflow?.id);
  };
  
  const renderField = (field) => {
    const { properties = [], units = [] } = context || {};
    const fieldKey = field.name || field.id;
    const value = formData[fieldKey] || field.defaultValue || '';
    const isPrefilled = workflow?.data?.[field.name] !== undefined && workflow?.data?.[field.name] !== null && workflow?.data?.[field.name] !== '';
    const confidence = workflow?.confidence?.[field.name];
    const hasConfidence = confidence && confidence > 0.7;
    
    // Check conditional fields
    if (field.conditional) {
      const conditionMet = formData[field.conditional.field] === field.conditional.value;
      if (!conditionMet) return null;
    }
    
    const baseInputClass = "w-full px-3 py-2 bg-gray-700/60 border border-gray-600/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 backdrop-blur-sm transition-all duration-200 text-sm";
    const prefilledClass = isPrefilled ? "border-green-500/80 ring-2 ring-green-500/30" : "";
    const confidenceClass = hasConfidence ? "ring-2 ring-green-500/30 border-green-500/50" : "";
    
    // 🔥 DEBUG: Log field rendering
    
    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
        return (
          <div key={field.name || field.id} className="mb-3">
            <label className="block text-xs text-gray-300 mb-1 flex items-center space-x-1">
              <span>{field.label}</span>
              {field.required && <span className="text-red-400">*</span>}
              {hasConfidence && <span className="text-green-400 text-xs">✓</span>}
            </label>
            <input
              type={field.type}
              value={value}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              className={`${baseInputClass} ${prefilledClass || confidenceClass}`}
              placeholder={`${field.label} eingeben...`}
              required={field.required}
            />
          </div>
        );
        
      case 'number':
        return (
          <div key={field.name || field.id} className="mb-3">
            <label className="block text-xs text-gray-300 mb-1 flex items-center space-x-1">
              <span>{field.label}</span>
              {field.required && <span className="text-red-400">*</span>}
              {hasConfidence && <span className="text-green-400 text-xs">✓</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={value}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              className={`${baseInputClass} ${prefilledClass || confidenceClass}`}
              placeholder={`${field.label} eingeben...`}
              required={field.required}
            />
          </div>
        );
        
      case 'date':
        return (
          <div key={field.name || field.id} className="mb-3">
            <label className="block text-xs text-gray-300 mb-1 flex items-center space-x-1">
              <span>{field.label}</span>
              {field.required && <span className="text-red-400">*</span>}
              {hasConfidence && <span className="text-green-400 text-xs">✓</span>}
            </label>
            <input
              type="date"
              value={value}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              className={`${baseInputClass} ${prefilledClass || confidenceClass}`}
              required={field.required}
            />
          </div>
        );
        
      case 'select':
        return (
          <div key={field.name || field.id} className="mb-3">
            <label className="block text-xs text-gray-300 mb-1 flex items-center space-x-1">
              <span>{field.label}</span>
              {field.required && <span className="text-red-400">*</span>}
              {hasConfidence && <span className="text-green-400 text-xs">✓</span>}
            </label>
            <select
              value={value}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              className={`${baseInputClass} ${prefilledClass || confidenceClass}`}
              required={field.required}
            >
              <option value="">{field.label} wählen...</option>
              {field.options?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
        
      case 'textarea':
        return (
          <div key={field.name || field.id} className="mb-3">
            <label className="block text-xs text-gray-300 mb-1 flex items-center space-x-1">
              <span>{field.label}</span>
              {field.required && <span className="text-red-400">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              className={`${baseInputClass} ${prefilledClass || confidenceClass} resize-none`}
              rows={3}
              placeholder={`${field.label} eingeben...`}
              required={field.required}
            />
          </div>
        );
        
      case 'property_select':
        return (
          <div key={fieldKey} className="flex flex-col space-y-1">
            <label className="text-xs text-gray-300">
              {field.label}
            </label>
            <select
              value={formData.property_id || ''}
              onChange={(e) => handleInputChange('property_id', e.target.value)}
              className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm"
              disabled={(context?.properties?.length ?? 0) === 0}
            >
              <option value="">Objekt wählen...</option>
              {(context?.properties ?? []).map(p => (
                <option key={p.id} value={p.id}>
                  {p.property_name || `${p.street} ${p.city}`}
                </option>
              ))}
            </select>
          </div>
        );

      case 'unit_select':
        const selectedProperty = formData.property_id;
        const filteredUnits = (units || []).filter(
          u => String(u.property_id) === String(selectedProperty)
        );
        return (
          <div key={fieldKey} className="flex flex-col space-y-1">
            <label className="text-xs text-gray-300">
              {field.label}
            </label>
            <select
              value={formData[fieldKey] || ''}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              disabled={!formData.property_id}
              className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm disabled:opacity-50"
            >
              <option value="">
                {!formData[fieldKey]
                  ? "Bitte zuerst Objekt wählen"
                  : "Einheit wählen..."}
              </option>
              {filteredUnits.map(u => (
                <option key={u.id} value={u.id}>
                  {u.unit_identifier}
                </option>
              ))}
            </select>
          </div>
        );
        
      default:
        console.warn('[WorkflowForm] Unknown field type:', field.type);
        return null;
    }
  };
  
  // DEBUG: Log what we're rendering
  if (workflow?.fields) {
  }
  if (cancelled) return null;
  
  return (
    <div className="mt-3 pt-3 border-t border-gray-600/30">
      {error && (
        <div className="mb-3 p-2 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-xs">⚠️ Fehler: Bitte füllen Sie alle Pflichtfelder aus.</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-2">
        {/* 🔥 DEBUG: Log fields before rendering */}
        {fieldsList && fieldsList.length > 0 ? (
          fieldsList.map(renderField)
        ) : (
          <div className="text-xs text-yellow-400 p-2 bg-yellow-900/20 rounded">
            ⚠️ Keine Formularfelder verfügbar
          </div>
        )}
        
        <div className="flex space-x-2 pt-2">
          <button
            type="submit"
            className="flex-1 px-3 py-2 bg-blue-600/80 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors flex items-center justify-center space-x-1"
          >
            <span>✓</span>
            <span>Erstellen</span>
          </button>
          <button
            type="button"
            onClick={() => { setCancelled(true); onCancel?.(); }}
            className="flex-1 px-3 py-2 bg-gray-600/80 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors flex items-center justify-center space-x-1"
          >
            <span>✗</span>
            <span>Abbrechen</span>
          </button>
        </div>
      </form>
    </div>
  );
};

// Workflow Summary Component - Shows data summary before final confirmation
const WorkflowSummary = ({ workflow, onConfirm, onCancel }) => {
  const handleConfirm = () => {
    onConfirm({
      workflowId: workflow.id,
      confirmed: true
    });
  };
  
  return (
    <div className="mt-3 pt-3 border-t border-gray-600/30">
      <div className="mb-3 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
        <h4 className="text-sm font-medium text-blue-300 mb-2">Zusammenfassung:</h4>
        <div className="text-xs text-gray-300 whitespace-pre-line">
          {workflow.data ? Object.entries(workflow.data)
            .filter(([key, value]) => value && value !== '')
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n') : 'Keine Daten'}
        </div>
      </div>
      
      <div className="flex space-x-2">
        <button
          onClick={handleConfirm}
          className="flex-1 px-3 py-2 bg-green-600/80 hover:bg-green-600 text-white text-sm rounded-lg transition-colors flex items-center justify-center space-x-1"
        >
          <span>✓</span>
          <span>Bestätigen</span>
        </button>
        <button
          onClick={onCancel}
          className="flex-1 px-3 py-2 bg-gray-600/80 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors flex items-center justify-center space-x-1"
        >
          <span>✗</span>
          <span>Abbrechen</span>
        </button>
      </div>
    </div>
  );
};

// Enhanced Loading Component with subtle animation
const EnhancedLoading = () => (
  <div className="flex justify-start mb-6">
    <div className="flex items-end space-x-3">
      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
        <Brain className="w-4 h-4 text-white animate-pulse" />
      </div>
      <div className="bg-gradient-to-r from-gray-800/60 to-gray-900/60 backdrop-blur-sm border border-gray-600/30 rounded-2xl px-4 py-3 max-w-xs">
        <div className="flex items-center space-x-2">
          <Sparkles className="w-4 h-4 text-blue-400 animate-spin" />
          <span className="text-gray-300 text-sm">Clara denkt...</span>
        </div>
        <div className="mt-2">
          <AnimatedWave isActive={true} />
        </div>
      </div>
    </div>
  </div>
);

// NEW: Intelligent Query Engine for Clara
const ClaraQueryEngine = {
  // Parse and classify different types of queries
  parseQuery(message, portfolioData) {
    const lowerMessage = message.toLowerCase();
    const result = {
      type: 'unknown',
      filters: {},
      targetProperty: null,
      targetCity: null,
      criteria: [],
      confidence: 0
    };

    // 1. EXACT ADDRESS RECOGNITION
    if (portfolioData && portfolioData.properties) {
      portfolioData.properties.forEach(property => {
        if (property.street) {
          const fullAddress = `${property.street}`.toLowerCase();
          const streetName = property.street.toLowerCase();
          
          // Extract house number from property street
          const houseNumberMatch = property.street.match(/(\d+)/);
          const houseNumber = houseNumberMatch ? houseNumberMatch[1] : null;
          
          // Check for exact address match (street + number)
          if (houseNumber) {
            const exactPattern = new RegExp(`${streetName.replace(/\d+/, '').trim()}\\s*${houseNumber}`, 'i');
            if (exactPattern.test(lowerMessage)) {
              result.type = 'exact_address';
              result.targetProperty = property;
              result.confidence = 0.95;
              return result;
            }
          }
          
          // Check for street name only (without conflicting numbers)
          const streetNameOnly = streetName.replace(/\d+/, '').trim();
          if (lowerMessage.includes(streetNameOnly)) {
            // Make sure no other house number is mentioned
            const mentionedNumbers = lowerMessage.match(/\d+/g);
            if (!mentionedNumbers || mentionedNumbers.length === 0) {
              result.type = 'street_only';
              result.targetProperty = property;
              result.confidence = 0.8;
            }
          }
        }
      });
    }

    // If exact address found, return immediately
    if (result.type === 'exact_address') {
      return result;
    }

    // NEW: Smart Suggestions for similar addresses
    if (portfolioData && portfolioData.properties) {
      const mentionedNumbers = lowerMessage.match(/\d+/g);
      if (mentionedNumbers && mentionedNumbers.length > 0) {
        const requestedNumber = mentionedNumbers[0];
        
        // Find similar street names
        portfolioData.properties.forEach(property => {
          if (property.street) {
            const streetNameOnly = property.street.replace(/\d+/, '').trim().toLowerCase();
            if (lowerMessage.includes(streetNameOnly)) {
              result.type = 'similar_address_suggestion';
              result.targetProperty = property;
              result.requestedAddress = `${streetNameOnly} ${requestedNumber}`;
              result.confidence = 0.9;
              return result;
            }
          }
        });
      }
    }

    // 2. CITY-BASED QUERIES
    const cityPatterns = [
      /in\s+(\w+)/,
      /(\w+)\s+(objekt|immobilie)/,
      /(mannheim|heidelberg|karlsruhe|stuttgart|frankfurt)/
    ];
    
    for (const pattern of cityPatterns) {
      const match = lowerMessage.match(pattern);
      if (match) {
        result.type = 'city_filter';
        result.targetCity = match[1] || match[0];
        result.confidence = 0.85;
        break;
      }
    }

    // 3. CRITERIA-BASED QUERIES
    const criteriaPatterns = [
      // Size criteria
      { pattern: /mehr\s+als\s+(\d+)\s*(m²|quadratmeter)/, type: 'min_area', extract: (m) => parseInt(m[1]) },
      { pattern: /weniger\s+als\s+(\d+)\s*(m²|quadratmeter)/, type: 'max_area', extract: (m) => parseInt(m[1]) },
      { pattern: /über\s+(\d+)\s*(m²|quadratmeter)/, type: 'min_area', extract: (m) => parseInt(m[1]) },
      { pattern: /unter\s+(\d+)\s*(m²|quadratmeter)/, type: 'max_area', extract: (m) => parseInt(m[1]) },
      
      // Occupancy criteria
      { pattern: /leer|leerstehend|unvermietet/, type: 'occupancy', extract: () => 'vacant' },
      { pattern: /vermietet|bewohnt|belegt/, type: 'occupancy', extract: () => 'occupied' },
      
      // Rent criteria
      { pattern: /miete\s+über\s+(\d+)/, type: 'min_rent', extract: (m) => parseInt(m[1]) },
      { pattern: /miete\s+unter\s+(\d+)/, type: 'max_rent', extract: (m) => parseInt(m[1]) },
      
      // Property type
      { pattern: /wohnung|apartment/, type: 'property_type', extract: () => 'apartment' },
      { pattern: /haus|einfamilienhaus/, type: 'property_type', extract: () => 'house' },
      { pattern: /gewerbe|büro/, type: 'property_type', extract: () => 'commercial' },
      
      // Year criteria
      { pattern: /baujahr\s+(\d{4})/, type: 'year_built', extract: (m) => parseInt(m[1]) },
      { pattern: /älter\s+als\s+(\d+)/, type: 'max_age', extract: (m) => new Date().getFullYear() - parseInt(m[1]) },
      { pattern: /neuer\s+als\s+(\d+)/, type: 'min_age', extract: (m) => new Date().getFullYear() - parseInt(m[1]) }
    ];

    criteriaPatterns.forEach(({ pattern, type, extract }) => {
      const match = lowerMessage.match(pattern);
      if (match) {
        result.criteria.push({
          type: type,
          value: extract(match)
        });
        result.type = result.type === 'unknown' ? 'criteria_filter' : result.type;
        result.confidence = Math.max(result.confidence, 0.8);
      }
    });

    // 4. PORTFOLIO OVERVIEW QUERIES
    const overviewPatterns = [
      /alle\s+(meine\s+)?(objekt|immobilie)/,
      /portfolio|übersicht|überblick/,
      /was\s+hast\s+du|was\s+kennst\s+du/,
      /zeig\s+mir/,
      /liste/
    ];

    for (const pattern of overviewPatterns) {
      if (pattern.test(lowerMessage)) {
        result.type = 'portfolio_overview';
        result.confidence = 0.9;
        break;
      }
    }

    return result;
  },

  // Apply filters to portfolio data based on parsed query
  applyFilters(portfolioData, queryResult) {
    if (!portfolioData) return null;

    let filteredProperties = [...portfolioData.properties];
    let filteredUnits = [...portfolioData.units];
    let filteredTenants = [...portfolioData.tenants];

    // Apply exact address filter
    if (queryResult.targetProperty) {
      filteredProperties = [queryResult.targetProperty];
      filteredUnits = portfolioData.units.filter(u => 
        u.properties?.id === queryResult.targetProperty.id
      );
      filteredTenants = portfolioData.tenants.filter(t => 
        t.units?.properties?.id === queryResult.targetProperty.id
      );
    }

    // Apply city filter
    if (queryResult.targetCity) {
      filteredProperties = filteredProperties.filter(p => 
        p.city && p.city.toLowerCase().includes(queryResult.targetCity.toLowerCase())
      );
      const propertyIds = filteredProperties.map(p => p.id);
      filteredUnits = filteredUnits.filter(u => 
        propertyIds.includes(u.properties?.id)
      );
      filteredTenants = filteredTenants.filter(t => 
        propertyIds.includes(t.units?.properties?.id)
      );
    }

    // Apply criteria filters
    queryResult.criteria.forEach(criterion => {
      switch (criterion.type) {
        case 'min_area':
          filteredUnits = filteredUnits.filter(u => (u.area || 0) >= criterion.value);
          break;
        case 'max_area':
          filteredUnits = filteredUnits.filter(u => (u.area || 0) <= criterion.value);
          break;
        case 'occupancy':
          if (criterion.value === 'vacant') {
            filteredUnits = filteredUnits.filter(u => !u.tenants || u.tenants.length === 0);
          } else if (criterion.value === 'occupied') {
            filteredUnits = filteredUnits.filter(u => u.tenants && u.tenants.length > 0);
          }
          break;
        case 'min_rent':
          filteredTenants = filteredTenants.filter(t => (t.total_rent || 0) >= criterion.value);
          break;
        case 'max_rent':
          filteredTenants = filteredTenants.filter(t => (t.total_rent || 0) <= criterion.value);
          break;
        case 'property_type':
          filteredProperties = filteredProperties.filter(p => 
            p.property_type && p.property_type.toLowerCase().includes(criterion.value)
          );
          break;
        case 'year_built':
          filteredProperties = filteredProperties.filter(p => p.year_built === criterion.value);
          break;
        case 'min_age':
          filteredProperties = filteredProperties.filter(p => (p.year_built || 0) >= criterion.value);
          break;
        case 'max_age':
          filteredProperties = filteredProperties.filter(p => (p.year_built || 0) <= criterion.value);
          break;
      }
    });

    return {
      properties: filteredProperties,
      units: filteredUnits,
      tenants: filteredTenants,
      queryResult: queryResult
    };
  },

  // Generate intelligent response based on filtered data
  generateResponse(filteredData, originalMessage) {
    if (!filteredData) {
      return 'Ihre Portfolio-Daten werden noch geladen. Bitte versuchen Sie es in einem Moment erneut.';
    }

    const { properties, units, tenants, queryResult } = filteredData;
    const lowerMessage = originalMessage.toLowerCase();

    // Smart address suggestions
    if (queryResult.type === 'similar_address_suggestion' && queryResult.targetProperty) {
      const property = queryResult.targetProperty;
      const propertyUnits = units.filter(u => u.properties?.id === property.id);
      const propertyTenants = tenants.filter(t => t.units?.properties?.id === property.id);

      let response = `${queryResult.requestedAddress} ist mir nicht bekannt. Meinten Sie ${property.street}?\n\n`;
      response += `Zu diesem Objekt kann ich Ihnen folgendes sagen:\n`;
      response += `${property.street}, ${property.city}\n`;
      
      // Add relevant details based on question type
      if (lowerMessage.includes('groß') || lowerMessage.includes('m²') || lowerMessage.includes('fläche')) {
        const totalArea = propertyUnits.reduce((sum, u) => sum + (u.area || 0), 0);
        response += `Gesamtfläche: ${totalArea} m²`;
      } else if (lowerMessage.includes('miete')) {
        const totalRent = propertyTenants.reduce((sum, t) => sum + (t.total_rent || 0), 0);
        response += totalRent > 0 ? `Mieteinnahmen: ${totalRent.toLocaleString()} €/Monat` : `Derzeit nicht vermietet`;
      } else {
        // General info
        response += `Typ: ${property.property_type || 'N/A'}, Baujahr: ${property.year_built || 'N/A'}`;
        if (propertyUnits.length > 0) {
          const totalArea = propertyUnits.reduce((sum, u) => sum + (u.area || 0), 0);
          response += `, ${totalArea} m²`;
        }
        if (propertyTenants.length > 0) {
          const totalRent = propertyTenants.reduce((sum, t) => sum + (t.total_rent || 0), 0);
          response += `, ${totalRent.toLocaleString()} €/Monat`;
        }
      }
      
      return response;
    }

    // Exact address responses
    if (queryResult.type === 'exact_address' && queryResult.targetProperty) {
      const property = queryResult.targetProperty;
      const propertyUnits = units.filter(u => u.properties?.id === property.id);
      const propertyTenants = tenants.filter(t => t.units?.properties?.id === property.id);

      let response = `${property.street}, ${property.city}:\n`;
      
      if (lowerMessage.includes('groß') || lowerMessage.includes('m²') || lowerMessage.includes('fläche')) {
        const totalArea = propertyUnits.reduce((sum, u) => sum + (u.area || 0), 0);
        response += `Gesamtfläche: ${totalArea} m²`;
        if (propertyUnits.length > 1) {
          response += ` (${propertyUnits.length} Einheiten)`;
        }
      } else if (lowerMessage.includes('miete')) {
        const totalRent = propertyTenants.reduce((sum, t) => sum + (t.total_rent || 0), 0);
        if (totalRent > 0) {
          response += `Mieteinnahmen: ${totalRent.toLocaleString()} €/Monat`;
        } else {
          response += `Derzeit nicht vermietet`;
        }
      } else if (lowerMessage.includes('mieter') || lowerMessage.includes('bewohner')) {
        if (propertyTenants.length > 0) {
          const tenantNames = propertyTenants.map(t => `${t.name}`).join(', ');
          response += `Mieter: ${tenantNames}`;
        } else {
          response += `Derzeit nicht vermietet`;
        }
      } else {
        // General property info
        response += `Typ: ${property.property_type || 'N/A'}, Baujahr: ${property.year_built || 'N/A'}`;
        if (propertyUnits.length > 0) {
          const totalArea = propertyUnits.reduce((sum, u) => sum + (u.area || 0), 0);
          response += `, ${totalArea} m²`;
        }
        if (propertyTenants.length > 0) {
          const totalRent = propertyTenants.reduce((sum, t) => sum + (t.total_rent || 0), 0);
          response += `, ${totalRent.toLocaleString()} €/Monat`;
        }
      }
      
      return response;
    }

    // City-based responses
    if (queryResult.type === 'city_filter') {
      if (properties.length === 0) {
        return `Sie haben keine Objekte in ${queryResult.targetCity} in Ihrem Portfolio.`;
      }
      
      let response = `Ihre Objekte in ${queryResult.targetCity} (${properties.length}):\n`;
      properties.forEach((p, index) => {
        response += `${index + 1}. ${p.street}`;
        const propertyUnits = units.filter(u => u.properties?.id === p.id);
        if (propertyUnits.length > 0) {
          const totalArea = propertyUnits.reduce((sum, u) => sum + (u.area || 0), 0);
          response += ` (${totalArea} m²)`;
        }
        response += '\n';
      });
      
      return response;
    }

    // Criteria-based responses
    if (queryResult.type === 'criteria_filter') {
      const criteriaDesc = queryResult.criteria.map(c => {
        switch (c.type) {
          case 'min_area': return `über ${c.value} m²`;
          case 'max_area': return `unter ${c.value} m²`;
          case 'occupancy': return c.value === 'vacant' ? 'leerstehend' : 'vermietet';
          case 'min_rent': return `Miete über ${c.value} €`;
          case 'max_rent': return `Miete unter ${c.value} €`;
          default: return `${c.type}: ${c.value}`;
        }
      }).join(', ');

      if (units.length === 0) {
        return `Keine Einheiten gefunden mit Kriterien: ${criteriaDesc}`;
      }

      let response = `Einheiten mit ${criteriaDesc} (${units.length}):\n`;
      units.forEach((unit, index) => {
        response += `${index + 1}. ${unit.properties?.street || 'Adresse unbekannt'}`;
        if (unit.unit_number) response += `, Einheit ${unit.unit_number}`;
        if (unit.area) response += ` (${unit.area} m²)`;
        
        const unitTenant = tenants.find(t => t.units?.id === unit.id);
        if (unitTenant) {
          response += ` - ${unitTenant.name}, ${unitTenant.total_rent} €/Monat`;
        } else {
          response += ` - Leer`;
        }
        response += '\n';
      });

      return response;
    }

    // Portfolio overview responses
    if (queryResult.type === 'portfolio_overview') {
      const totalArea = units.reduce((sum, u) => sum + (u.area || 0), 0);
      const totalRent = tenants.reduce((sum, t) => sum + (t.total_rent || 0), 0);
      const occupiedUnits = units.filter(u => u.tenants && u.tenants.length > 0).length;
      const occupancyRate = units.length > 0 ? (occupiedUnits / units.length * 100).toFixed(1) : 0;

      let response = `Ihr Portfolio-Überblick:\n`;
      response += `${properties.length} Objekte, ${units.length} Einheiten, ${tenants.length} Mieter\n`;
      response += `Gesamtfläche: ${totalArea.toLocaleString()} m²\n`;
      response += `Mieteinnahmen: ${totalRent.toLocaleString()} €/Monat\n`;
      response += `Belegungsgrad: ${occupancyRate}%\n\n`;
      
      response += `Ihre Objekte:\n`;
      properties.forEach((p, index) => {
        response += `${index + 1}. ${p.street}, ${p.city}`;
        const propertyUnits = units.filter(u => u.properties?.id === p.id);
        const propertyTenants = tenants.filter(t => t.units?.properties?.id === p.id);
        if (propertyUnits.length > 0) {
          const totalArea = propertyUnits.reduce((sum, u) => sum + (u.area || 0), 0);
          response += ` (${totalArea} m²)`;
        }
        if (propertyTenants.length > 0) {
          const totalRent = propertyTenants.reduce((sum, t) => sum + (t.total_rent || 0), 0);
          response += ` - ${totalRent.toLocaleString()} €/Monat`;
        } else {
          response += ` - Leer`;
        }
        response += '\n';
      });

      return response;
    }

    // Default: Basic portfolio info
    const totalRent = tenants.reduce((sum, t) => sum + (t.total_rent || 0), 0);
    return `Portfolio-Überblick: ${properties.length} Objekte, ${tenants.length} Mieter, ${totalRent.toLocaleString()} €/Monat`;
  }
};

// DUAL LAYER INTELLIGENCE - SUPABASE + MISTRAL INTEGRATION
const ClaraIntelligenceService = {
  async getSupabaseContext(userId, message, portfolioData = null) {
    try {
      console.log('🔍 Collecting user-specific Supabase context for user:', userId, 'message:', message);

      // Sicherheitscheck: Nur authentifizierte Benutzer
      if (!userId) {
        console.warn('⚠️ No authenticated user - returning empty context');
        return '';
      }

      // NEW: If no portfolioData provided, load directly like Dashboard does
      if (!portfolioData) {
        console.log('🔄 No portfolioData available, loading directly from Supabase...');
        
        try {
          // Load data directly like Dashboard does with REAL SUPABASE COLUMNS
          const [propertiesRes, tenantsRes] = await Promise.all([
            masterSupabase.from('properties').select('id, property_name, street, city, postal_code, property_type, monthly_rent_total, units, purchase_price, property_status, owner_id'),
            masterSupabase.from('tenants').select('id, property_id, name, unit_identifier, email, monthly_rent, cold_rent, tenant_status, lease_start, lease_end')
          ]);
          
          if (propertiesRes.error) {
            console.error('❌ Error loading properties:', propertiesRes.error);
          }
          if (tenantsRes.error) {
            console.error('❌ Error loading tenants:', tenantsRes.error);
          }

          const properties = propertiesRes.data || [];
          const tenants = tenantsRes.data || [];
          
          console.log('✅ Direct load successful:', properties.length, 'properties,', tenants.length, 'tenants');
          
          // OPTIMIZED CONTEXT PIPELINE: Smart Template Rendering
          const getReducedPortfolioContext = (properties, tenants, userMessage) => {
            // Top 3 Properties nach monthly_rent_total (financial relevance)
            const topProperties = properties
              .filter(p => p.monthly_rent_total && p.monthly_rent_total > 0)
              .sort((a, b) => (b.monthly_rent_total || 0) - (a.monthly_rent_total || 0))
              ; // FULL CONTEXT: All properties
              
            // Top 5 Tenants nach monthly_rent
            const topTenants = tenants
              .filter(t => t.monthly_rent && t.monthly_rent > 0)
              .sort((a, b) => (b.monthly_rent || 0) - (a.monthly_rent || 0))
              ; // FULL CONTEXT: All tenants
            
            // GERMAN OUTPUT despite English column names
            const context = `📍 Portfolio-Übersicht: ${properties.length} Objekte, ${tenants.length} Mieter

Wichtigste Objekte:
${topProperties.map(p => `• ${p.property_name || p.street} in ${p.city} - ${p.monthly_rent_total?.toLocaleString()}€ Monatsmiete (${p.units || 'N/A'} Einheiten)`).join('\n')}

Hauptmieter:
${topTenants.map(t => `• ${t.name} zahlt ${t.monthly_rent?.toLocaleString()}€ monatlich`).join('\n')}`;

            console.log('✅ Full context generated:', context.length, 'characters (optimized for Gemini)');
            return context;
          };
          
          // INTELLIGENT QUESTION ANALYSIS
          const lowerMessage = message.toLowerCase();
          
          if (lowerMessage.includes('objekt') || lowerMessage.includes('immobil')) {
            // Check for specific property
            const specificProperty = properties.find(p => {
              const propertyText = `${p.street || ''} ${p.city || ''} ${p.property_name || ''}`.toLowerCase();
              return lowerMessage.split(' ').some(word => 
                word.length > 3 && propertyText.includes(word)
              );
            });
            
            if (specificProperty) {
              // Specific property details in German
              const propertyTenants = tenants.filter(t => t.property_id === specificProperty.id);
              return `Objekt: ${specificProperty.property_name || specificProperty.street} in ${specificProperty.city}

Details:
- Typ: ${specificProperty.property_type || 'Nicht angegeben'}
- Einheiten: ${specificProperty.units || 'N/A'}
- Gesamtmiete: ${specificProperty.monthly_rent_total?.toLocaleString() || 'N/A'}€
- Status: ${specificProperty.property_status || 'N/A'}
- Mieter: ${propertyTenants.length}
${propertyTenants.map(t => `  • ${t.name} - ${t.monthly_rent?.toLocaleString() || 'N/A'}€`).join('\n')}`;
            }
            
            if (lowerMessage.includes('wie viele') || lowerMessage.includes('anzahl')) {
              return `Sie haben ${properties.length} Objekte in Ihrem Portfolio.`;
            }
            
            // Use reduced context for Mistral
            return getReducedPortfolioContext(properties, tenants, message);
          }
          
          if (lowerMessage.includes('mieter') || lowerMessage.includes('tenant')) {
            // Check for specific tenant
            const specificTenant = tenants.find(t => {
              const tenantName = t.name.toLowerCase();
              return lowerMessage.split(' ').some(word => 
                word.length > 2 && tenantName.includes(word)
              );
            });
            
            if (specificTenant) {
              const tenantProperty = properties.find(p => p.id === specificTenant.property_id);
              return `Mieter: ${specificTenant.name}

Details:
- Miete: ${specificTenant.monthly_rent?.toLocaleString() || 'N/A'}€
- Kaltmiete: ${specificTenant.cold_rent?.toLocaleString() || 'N/A'}€
- Objekt: ${tenantProperty?.property_name || tenantProperty?.street || 'N/A'} in ${tenantProperty?.city || ''}
- Status: ${specificTenant.tenant_status || 'N/A'}
- E-Mail: ${specificTenant.email || 'Nicht hinterlegt'}`;
            }
            
            if (lowerMessage.includes('wie viele') || lowerMessage.includes('anzahl')) {
              return `Sie haben ${tenants.length} aktive Mieter.`;
            }
            
            // Use reduced context for Mistral
            return getReducedPortfolioContext(properties, tenants, message);
          }
          
          // For other questions, use minimal optimized context for Mistral
          return getReducedPortfolioContext(properties, tenants, message);
          
        } catch (directLoadError) {
          console.error('❌ Direct load failed:', directLoadError);
          // Fall back to original keyword-based logic
        }
      }

      // ORIGINAL: Use intelligent query parsing if portfolio data is available
      if (portfolioData) {
        console.log('⚡ Using intelligent query engine with cached data');
        
        const queryResult = ClaraQueryEngine.parseQuery(message, portfolioData);
        console.log('🎯 Query parsed:', queryResult);
        
        const filteredData = ClaraQueryEngine.applyFilters(portfolioData, queryResult);
        const response = ClaraQueryEngine.generateResponse(filteredData, message);
        
        console.log('✅ Intelligent response generated:', response.length, 'characters');
        return response;
      }

      // FALLBACK: Original keyword-based logic for live queries
      console.log('🔄 No cache available, falling back to keyword-based live queries');
      
      const lowerMessage = message.toLowerCase();
      let context = '';

      // Immobilien-Kontext sammeln (nur für authentifizierten Benutzer)
      if (lowerMessage.includes('immobilie') || lowerMessage.includes('objekt') || 
          lowerMessage.includes('straße') || lowerMessage.includes('haus') ||
          lowerMessage.includes('property') || lowerMessage.includes('gebäude')) {
        
        const { data: properties, error } = await masterSupabase
          .from('properties')
          .select('*')
          .eq('owner_id', userId); // 🔒 Nur Objekte des authentifizierten Benutzers
        
        if (error) {
          console.error('❌ Error fetching user properties:', error);
        } else if (properties && properties.length > 0) {
          context += `\n\nIHRE IMMOBILIEN-PORTFOLIO (${properties.length} Objekte):\n`;
          properties.forEach((p, index) => {
            context += `${index + 1}. ${p.street || 'Straße unbekannt'}, ${p.postal_code || ''} ${p.city || 'Stadt unbekannt'}\n`;
            context += `   - Kaufpreis: ${p.purchase_price?.toLocaleString() || 'N/A'} €\n`;
            context += `   - Typ: ${p.property_type || 'N/A'}\n`;
            context += `   - Baujahr: ${p.year_built || 'N/A'}\n\n`;
          });
        } else {
          context += `\n\nSie haben noch keine Immobilien in Ihrem Portfolio registriert.\n`;
        }
      }

      // Mieter-Kontext sammeln (nur für Objekte des Benutzers)
      if (lowerMessage.includes('mieter') || lowerMessage.includes('bewohner') || 
          lowerMessage.includes('miet') || lowerMessage.includes('tenant')) {
        
        const { data: tenants, error } = await masterSupabase
          .from('tenants')
          .select(`
            *,
            units (
              unit_number,
              properties!inner (street, postal_code, city, owner_id)
            )
          `)
          .eq('units.properties.owner_id', userId); // 🔒 Nur Mieter in Objekten des Benutzers
        
        if (error) {
          console.error('❌ Error fetching user tenants:', error);
        } else if (tenants && tenants.length > 0) {
          context += `\n\nIHRE MIETER-ÜBERSICHT (${tenants.length} Mieter):\n`;
          tenants.forEach((t, index) => {
            context += `${index + 1}. ${t.name}\n`;
            context += `   - Miete: ${t.total_rent?.toLocaleString() || 'N/A'} €/Monat\n`;
            context += `   - Objekt: ${t.units?.properties?.street || 'Adresse unbekannt'}\n`;
            context += `   - Einheit: ${t.units?.unit_number || 'N/A'}\n\n`;
          });
          
          const totalRent = tenants.reduce((sum, t) => sum + (t.total_rent || 0), 0);
          context += `IHRE GESAMT-MIETEINNAHMEN: ${totalRent.toLocaleString()} €/Monat (${(totalRent * 12).toLocaleString()} €/Jahr)\n`;
        } else {
          context += `\n\nSie haben noch keine Mieter in Ihren Objekten registriert.\n`;
        }
      }

      // Einheiten-Kontext sammeln (Leerstand-Analyse)
      if (lowerMessage.includes('einheit') || lowerMessage.includes('unit') || 
          lowerMessage.includes('leerstand') || lowerMessage.includes('belegung') ||
          lowerMessage.includes('wohnung') || lowerMessage.includes('apartment')) {
        
        const { data: units, error } = await masterSupabase
          .from('units')
          .select(`
            *,
            properties!inner (street, postal_code, city, owner_id),
            tenants (name, total_rent)
          `)
          .eq('properties.owner_id', userId); // 🔒 Nur Einheiten in Objekten des Benutzers
        
        if (error) {
          console.error('❌ Error fetching user units:', error);
        } else if (units && units.length > 0) {
          const occupiedUnits = units.filter(u => u.tenants && u.tenants.length > 0);
          const vacantUnits = units.filter(u => !u.tenants || u.tenants.length === 0);
          const occupancyRate = (occupiedUnits.length / units.length * 100).toFixed(1);
          
          context += `\n\nIHRE EINHEITEN-ÜBERSICHT (${units.length} Einheiten):\n`;
          context += `- Vermietete Einheiten: ${occupiedUnits.length}\n`;
          context += `- Leerstehende Einheiten: ${vacantUnits.length}\n`;
          context += `- Belegungsgrad: ${occupancyRate}%\n\n`;
          
          if (vacantUnits.length > 0) {
            context += `LEERSTEHENDE EINHEITEN:\n`;
            vacantUnits.forEach((u, index) => {
              context += `${index + 1}. ${u.properties?.street || 'Adresse unbekannt'} - Einheit ${u.unit_number || 'N/A'}\n`;
              context += `   - Größe: ${u.size || 'N/A'} m²\n`;
              context += `   - Sollmiete: ${u.target_rent?.toLocaleString() || 'N/A'} €/Monat\n\n`;
            });
            
            const potentialRent = vacantUnits.reduce((sum, u) => sum + (u.target_rent || 0), 0);
            context += `POTENTIELLE ZUSÄTZLICHE MIETEINNAHMEN: ${potentialRent.toLocaleString()} €/Monat\n`;
          }
          
          if (occupiedUnits.length > 0) {
            context += `\nVERMIETETE EINHEITEN:\n`;
            occupiedUnits.forEach((u, index) => {
              const tenant = u.tenants[0];
              context += `${index + 1}. ${u.properties?.street || 'Adresse unbekannt'} - Einheit ${u.unit_number || 'N/A'}\n`;
              context += `   - Mieter: ${tenant?.name || 'Unbekannt'}\n`;
              context += `   - Miete: ${tenant?.total_rent?.toLocaleString() || 'N/A'} €/Monat\n`;
              context += `   - Größe: ${u.size || 'N/A'} m²\n\n`;
            });
          }
        } else {
          context += `\n\nSie haben noch keine Einheiten in Ihren Objekten registriert.\n`;
        }
      }

      // Wartungs-Kontext sammeln (nur für Objekte des Benutzers)
      if (lowerMessage.includes('wartung') || lowerMessage.includes('reparatur') || 
          lowerMessage.includes('instandhaltung') || lowerMessage.includes('maintenance')) {
        
        const { data: maintenance, error } = await masterSupabase
          .from('maintenance')
          .select(`
            *,
            properties!inner (street, postal_code, city, owner_id)
          `)
          .eq('properties.owner_id', userId) // 🔒 Nur Wartungen für Objekte des Benutzers
          .order('created_at', { ascending: false })
          .limit(10);
        
        if (error) {
          console.error('❌ Error fetching user maintenance:', error);
        } else if (maintenance && maintenance.length > 0) {
          context += `\n\nIHRE WARTUNGEN/INSTANDHALTUNG (${maintenance.length} Einträge):\n`;
          maintenance.forEach((m, index) => {
            context += `${index + 1}. ${m.title || m.description || 'Wartung'}\n`;
            context += `   - Status: ${m.status || 'N/A'}\n`;
            context += `   - Kosten: ${m.estimated_cost?.toLocaleString() || m.cost?.toLocaleString() || 'N/A'} €\n`;
            context += `   - Objekt: ${m.properties?.street || 'Objekt unbekannt'}\n\n`;
          });
        } else {
          context += `\n\nSie haben noch keine Wartungen für Ihre Objekte registriert.\n`;
        }
      }

      // Finanz-Kontext sammeln (nur für Objekte des Benutzers)
      if (lowerMessage.includes('finanz') || lowerMessage.includes('einnahmen') || 
          lowerMessage.includes('rendite') || lowerMessage.includes('gewinn')) {
        
        // Kombiniere Properties und Tenants für benutzer-spezifische Finanzanalyse
        const [propertiesRes, tenantsRes] = await Promise.all([
          masterSupabase.from('properties').select('*').eq('owner_id', userId),
          masterSupabase.from('tenants').select(`
            total_rent,
            units (
              properties!inner (owner_id)
            )
          `).eq('units.properties.owner_id', userId)
        ]);
        
        const properties = propertiesRes.data || [];
        const tenants = tenantsRes.data || [];
        
        if (properties.length > 0) {
          const totalPurchasePrice = properties.reduce((sum, p) => sum + (p.purchase_price || 0), 0);
          const totalMonthlyRent = tenants.reduce((sum, t) => sum + (t.total_rent || 0), 0);
          const totalYearlyRent = totalMonthlyRent * 12;
          const grossYield = totalPurchasePrice > 0 ? ((totalYearlyRent / totalPurchasePrice) * 100).toFixed(2) : 'N/A';
          
          context += `\n\nIHRE FINANZ-ÜBERSICHT:\n`;
          context += `- Ihre Gesamtinvestition: ${totalPurchasePrice.toLocaleString()} €\n`;
          context += `- Ihre monatlichen Mieteinnahmen: ${totalMonthlyRent.toLocaleString()} €\n`;
          context += `- Ihre jährlichen Mieteinnahmen: ${totalYearlyRent.toLocaleString()} €\n`;
          context += `- Ihre Bruttorendite: ${grossYield}%\n`;
          context += `- Ihre Anzahl Objekte: ${properties.length}\n`;
          context += `- Ihre Anzahl Mieter: ${tenants.length}\n`;
        } else {
          context += `\n\nSie haben noch keine Immobilien für eine Finanzanalyse registriert.\n`;
        }
      }

      console.log('✅ User-specific Supabase context collected:', context.length, 'characters');
      return context;
    } catch (error) {
      console.error('❌ Error getting user-specific Supabase context:', error);
      return '';
    }
  },

  async queryMistral(prompt, context = '') {
    try {
      console.log('🤖 Querying local Mistral on VPS...');
      
      // NEW: Dynamic Token Limits based on question complexity
      let maxTokens = 200; // Default
      const promptLength = prompt.length;
      const contextLength = context.length;
      
      if (promptLength < 30) {
        maxTokens = 150; // Short questions
      } else if (promptLength < 80) {
        maxTokens = 250; // Medium questions
      } else {
        maxTokens = 400; // Long/complex questions
      }
      
      // Adjust for context size
      if (contextLength > 1000) {
        maxTokens += 100; // More tokens for rich context
      }
      
      console.log(`🎯 Dynamic token limit: ${maxTokens} (prompt: ${promptLength} chars, context: ${contextLength} chars)`);
      
      const systemPrompt = `Du bist Clara, eine professionelle KI-Assistentin für Immobilienverwaltung. 
Du hilfst bei der Verwaltung von Immobilien, Mietern, Wartungen und Finanzen.

Antworte immer auf Deutsch, eloquent und natürlich.
KEINE Sternchen, Symbole oder Listen-Formatierung verwenden.
Verwende die bereitgestellten Daten aus der Supabase-Datenbank für präzise Antworten.
Gib konkrete Zahlen und Fakten an, wenn sie verfügbar sind.
Mache Empfehlungen basierend auf den Daten.
Halte Antworten prägnant aber informativ.

${context ? `AKTUELLE DATEN AUS DER DATENBANK:${context}` : ''}

Beantworte die Frage basierend auf den verfügbaren Daten. Wenn keine spezifischen Daten verfügbar sind, gib allgemeine Hilfe zur Immobilienverwaltung.`;

      const startTime = Date.now();
      const { data: { session } } = await masterSupabase.auth.getSession();
      const accessToken = session?.access_token;
      const isRentPaymentQuestion = /mieteingang|miete bezahlt|miete nicht bezahlt|zahlungsrückstand|mietrückstand|wer hat gezahlt|wer hat nicht gezahlt|offene mieten/i.test(String(prompt || ''));
      // Operative Bankfragen sind keine Dokumentrecherche: sie laufen immer
      // deterministisch über dieselbe Capital-Capability wie das Command Center.
      const response = await fetch(isRentPaymentQuestion ? '/api/capabilities/prepare' : '/api/ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}`, 'x-supabase-access-token': accessToken }),
        },
        body: JSON.stringify(isRentPaymentQuestion
          ? { capability: 'get_rent_payment_status', args: { months: 1, status_filter: 'all' } }
          : { question: prompt, preferredLLM: selectedModel })
      });
      if (!response.ok) throw new Error('Clara business request failed');
      const data = await response.json();
      const processingTime = Date.now() - startTime;
      console.log(`✅ Clara business response in ${processingTime}ms`);
      const responseText = (data.answer ?? '').trim() ||
        (data.response ?? '').trim() ||
        'Entschuldigung, es gab ein Problem bei der Antwort.';
      return {
        success: true,
        response: responseText,
        source: 'clara_business_api',
        processing_time: processingTime
      };
    } catch (error) {
      console.error('❌ Clara API query failed:', error);
      return {
        success: false,
        error: error.message,
        response: 'Entschuldigung, ich kann diese Frage momentan nicht beantworten. Das Clara-System ist nicht verfügbar.'
      };
    }
  }
};

// Main Clara KI Component
const ClaraKIPage = () => {
  const [messages, setMessages] = useState([]);
  const [activeArtifact, setActiveArtifact] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [supabaseStatus, setSupabaseStatus] = useState('connected');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [selectedModel, setSelectedModel] = useState('clara-auto'); // Canonical catalogue default

  // 🔥 Update Message State (zentral für alle Updates)
  const updateMessage = (id, patch) => {
    setMessages(prev =>
      prev.map(msg =>
        msg.id === id
          ? { ...msg, ...patch }
          : msg
      )
    );
  };

  const [uploadModalOpen, setUploadModalOpen] = useState(false); // NEW: Upload Modal State
  const chatContainerRef = useRef(null);
  const inFlightRef = useRef(false);
  const formRef = useRef(null); // NEW: Form ref for single trigger
  const autoSendTimeoutRef = useRef(null); // NEW: Auto-send timeout ref
  
  // Get authenticated user AND Portfolio Precache
  const { user, isAuthenticated, portfolioData, portfolioLoading, portfolioError, portfolioStats, isSuperAdmin } = useAuth();
  const { properties, loading: propsLoading, error: propsError } = useProperties();
  const { units, loading: unitsLoading, error: unitsError } = useUnits();

  // 🔍 Debug: Properties laden
  useEffect(() => {
    if (propsLoading) {
      console.log('[ClaraKIPage] Properties loading...');
    }
    if (propsError) {
      console.error('[ClaraKIPage] Properties error:', propsError);
    }
    if (properties && properties.length > 0) {
      console.log('[ClaraKIPage] Properties loaded:', properties.length, properties);
    }
  }, [propsLoading, propsError, properties]);

  // Working Voice Recognition Hook
  const {
    isListening,
    transcript,
    clearTranscript,
    startListening,
    stopListening,
    error: voiceError,
    confidence,
    isSupported: voiceSupported
  } = useVoiceRecognition();

  // Conversation Persistence Hook
  const {
    conversationId,
    isLoadingConversation,
    conversationError,
    initializeConversation,
    saveMessage,
    clearConversation,
    resetConversation,
    archiveConversation,
    searchMessages
  } = useConversationPersistence(user, isAuthenticated);

  // Clear chat function - defined AFTER hook to access resetConversation
  const clearChat = useCallback(async () => {
    if (window.confirm('Möchten Sie den Chat-Verlauf wirklich löschen? Dies startet eine neue Conversation.')) {
      try {
        console.log('[CHAT] Clearing chat completely...');
        await resetConversation();  // ✅ Reset conversation ID & clear DB
        setMessages([]);            // ✅ Clear UI
        await initializeConversation(); // ✅ Start fresh session
        console.log('[CHAT] Chat cleared and new conversation started');
      } catch (error) {
        console.error('[CHAT] Error clearing chat:', error);
      }
    }
  }, [resetConversation, initializeConversation]);

  // Update input value when transcript changes
  // Initialize Speech Output (Text-to-Speech)
  useEffect(() => {
    const initSpeechOutput = () => {
      if ('speechSynthesis' in window) {
        const loadVoices = () => {
          const voices = speechSynthesis.getVoices();
          // Filter für deutsche weibliche Stimmen
          const germanFemaleVoices = voices.filter(voice => 
            voice.lang.startsWith('de') && 
            (voice.name.toLowerCase().includes('female') || 
             voice.name.toLowerCase().includes('clara') ||
             voice.name.toLowerCase().includes('anna') ||
             voice.name.toLowerCase().includes('google') ||
             voice.name.toLowerCase().includes('deutsch'))
          ); // FULL CONTEXT: All properties // Max 3 Stimmen
          
          setAvailableVoices(germanFemaleVoices);
          if (germanFemaleVoices.length > 0) {
            setSelectedVoice(germanFemaleVoices[0]);
          }
        };

        loadVoices();
        speechSynthesis.onvoiceschanged = loadVoices;
      }
    };

    initSpeechOutput();
  }, []);

  // Load conversation on mount
  useEffect(() => {
    const loadConversation = async () => {
      console.log('[DEBUG] loadConversation called');
      console.log('[DEBUG] isAuthenticated:', isAuthenticated);
      console.log('[DEBUG] user:', user);
      console.log('[DEBUG] initializeConversation:', typeof initializeConversation);
      
      // ALWAYS try to initialize conversation, regardless of auth state
      // This ensures conversationId is created even if auth is delayed
      try {
        console.log('🚀 Attempting to initialize conversation...');
        const savedMessages = await initializeConversation();
        
        if (savedMessages && savedMessages.length > 0) {
          console.log('✅ Supabase connected successfully (masterSupabase client)');
          setSupabaseStatus('connected');
          // DEDUPLICATION: Remove duplicate welcome messages
          const welcomeMessageContent = 'Hallo! Ich bin Clara, Ihre KI-Assistentin für die Immobilienverwaltung. Ich kann Ihnen bei Fragen zu Ihren Objekten, Mietern und Finanzen helfen. Sie können mit mir sprechen oder schreiben.';
          
          // Find all welcome messages
          const welcomeMessages = savedMessages.filter(msg => 
            msg.type === 'assistant' && msg.content === welcomeMessageContent
          );
          
          // Keep only the first welcome message, remove duplicates
          let deduplicatedMessages = savedMessages;
          if (welcomeMessages.length > 1) {
            console.log(`⚠️  Found ${welcomeMessages.length} duplicate welcome messages, keeping only the first one`);
            const firstWelcomeId = welcomeMessages[0].id;
            deduplicatedMessages = savedMessages.filter(msg => 
              !(msg.type === 'assistant' && msg.content === welcomeMessageContent && msg.id !== firstWelcomeId)
            );
          }
          
          // Restore deduplicated conversation
          setMessages(deduplicatedMessages);
          console.log(`✅ Restored ${deduplicatedMessages.length} messages from database (${savedMessages.length - deduplicatedMessages.length} duplicates removed)`);
        } else {
          // Initialize with welcome message
          const welcomeMessage = {
            id: Date.now(),
            type: 'assistant',
            content: 'Hallo! Ich bin Clara, Ihre KI-Assistentin für die Immobilienverwaltung. Ich kann Ihnen bei Fragen zu Ihren Objekten, Mietern und Finanzen helfen. Sie können mit mir sprechen oder schreiben.',
            timestamp: new Date().toISOString()
          };
          setMessages([welcomeMessage]);
          
          // Save welcome message
          await saveMessage(welcomeMessage);
        }
      } catch (error) {
        console.error('[ERROR] Failed to initialize conversation:', error);
        // Continue anyway - conversation will be created on first message
      }
    };
    
    loadConversation();
  }, [isAuthenticated, user, initializeConversation, saveMessage]);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Check if message is the last Clara message
  const isLastClaraMessage = useCallback((messageIndex) => {
    const claraMessages = messages
      .map((msg, index) => ({ ...msg, originalIndex: index }))
      .filter(msg => msg.type === 'assistant');
    
    if (claraMessages.length === 0) return false;
    
    const lastClaraMessage = claraMessages[claraMessages.length - 1];
    return messageIndex === lastClaraMessage.originalIndex;
  }, [messages]);

  // HTML and Markdown stripping function for speech output
  const stripHtmlTags = useCallback((text) => {
    return text
      .replace(/<[^>]*>/g, '')                    // Remove HTML tags
      .replace(/&nbsp;/g, ' ')                    // Replace non-breaking spaces
      .replace(/&amp;/g, '&')                     // Replace HTML entities
      .replace(/\*\*/g, '')                       // Remove double asterisks (bold markdown)
      .replace(/\*/g, '')                         // Remove single asterisks (italic markdown)
      .replace(/(\d{4})\./g, '$1')                // Fix date pronunciation: "2022." -> "2022"
      .replace(/(\d{4})ste/g, '$1')               // Fix ordinal numbers: "2022ste" -> "2022"
      .replace(/\s+/g, ' ')                       // Normalize multiple spaces
      .trim();
  }, []);

  // Mobile-Erkennung für VoiceFormatter
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Speech Output Functions
  // ARCHITEKTUR: LLM → vollständige Chat-Antwort (unverändert) → VoiceFormatter → TTSService
  // chatText wird NIEMALS verändert — nur spokenText wird transformiert
  const speakText = useCallback((text, userMessage = '', mode = null) => {
    if (!text) return;
    TTSService.stop();
    const responseMode = mode || detectResponseMode(userMessage || '');
    const { spokenText } = formatForVoice(text, {
      mode: responseMode,
      isMobile,
      isVoice: true,
    });
    setIsSpeaking(true);
    TTSService.speak(spokenText)
      .then(() => setIsSpeaking(false))
      .catch(() => {
        // Fallback auf Browser-TTS wenn Server nicht erreichbar
        if ('speechSynthesis' in window && selectedVoice) {
          speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(spokenText);
          utterance.voice = selectedVoice;
          utterance.lang = 'de-DE';
          utterance.rate = 1.1;
          utterance.onend = () => setIsSpeaking(false);
          utterance.onerror = () => setIsSpeaking(false);
          speechSynthesis.speak(utterance);
        } else {
          setIsSpeaking(false);
        }
      });
  }, [selectedVoice, isMobile]);

  const stopSpeaking = useCallback(() => {
    speechSynthesis.cancel();
    setIsSpeaking(false);
  }, []);

  // ============================================================================
  // BankReport Helper Functions
  // ============================================================================

  const extractPropertyName = (message) => {
    const pattern1 = /für\s+([A-ZÄÖÜ][a-zäöüß]+(?:straße|strasse|str\.|weg|platz|allee|gasse)\s+\d+[a-z]?)/i;
    let match = message.match(pattern1);
    if (match) return match[1];
    
    const pattern2 = /(?:bankreport|report|bericht)\s+([A-ZÄÖÜ][a-zäöüß]+(?:straße|strasse|str\.|weg|platz|allee|gasse)\s+\d+[a-z]?)/i;
    match = message.match(pattern2);
    if (match) return match[1];
    
    const pattern3 = /([A-ZÄÖÜ][a-zäöüß]+(?:straße|strasse|str\.|weg|platz|allee|gasse))/i;
    match = message.match(pattern3);
    if (match) return match[1];
    
    return null;
  };

  const findPropertyByFuzzyName = (searchTerm, properties) => {
    if (!searchTerm || !properties || properties.length === 0) return null;
    
    const lowerSearch = searchTerm.toLowerCase();
    
    let match = properties.find(p => p.property_name.toLowerCase() === lowerSearch);
    if (match) return match;
    
    match = properties.find(p => p.property_name.toLowerCase().includes(lowerSearch));
    if (match) return match;
    
    match = properties.find(p => {
      const propertyWords = p.property_name.toLowerCase().split(/\s+/);
      const searchWords = lowerSearch.split(/\s+/);
      return searchWords.some(sw => propertyWords.some(pw => pw.includes(sw)));
    });
    
    return match || null;
  };

  const formatPropertyList = (properties) => {
    if (!properties || properties.length === 0) {
      return "Keine Immobilien gefunden.";
    }
    
    const lines = properties.map((p, i) => 
      `${i + 1}. **${p.property_name}** (${p.city}) - ${p.units} Einheiten`
    );
    
    return `Für welche Immobilie soll ich einen Bankreport erstellen?\n\n${lines.join('\n')}`;
  };

  // ============================================================================
  // Handle BankReport Request
  // ============================================================================

  const handleBankReportRequest = useCallback(async (message) => {
    console.log('[BANKREPORT] Processing request:', message);
    
    const userMessage = {
      id: crypto.randomUUID(),
      type: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    
    const loadingMessage = {
      id: crypto.randomUUID(),
      type: 'assistant',
      content: '🏦 Erstelle Bankreport...',
      isLoading: true,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage, loadingMessage]);
    await saveMessage(userMessage);
    
    try {
      const propertyName = extractPropertyName(message);
      console.log('[BANKREPORT] Extracted property name:', propertyName);
      
      if (!propertyName) {
        console.log('[BANKREPORT] No property name found, fetching list...');
        
        const propertiesResponse = await fetch('/api/reports/properties');
        const propertiesData = await propertiesResponse.json();
        
        if (!propertiesData.success || !propertiesData.properties || propertiesData.properties.length === 0) {
          throw new Error('Keine Immobilien in der Datenbank gefunden.');
        }
        
        const propertyListText = formatPropertyList(propertiesData.properties);
        
        const askMessage = {
          id: loadingMessage.id,
          type: 'assistant',
          content: propertyListText,
          timestamp: new Date().toISOString(),
          isLoading: false
        };
        
        setMessages(prev => prev.map(msg => 
          msg.id === loadingMessage.id ? askMessage : msg
        ));
        await saveMessage(askMessage);
        return;
      }
      
      console.log('[BANKREPORT] Fetching properties for matching...');
      const propertiesResponse = await fetch('/api/reports/properties');
      const propertiesData = await propertiesResponse.json();
      
      if (!propertiesData.success) {
        throw new Error('Fehler beim Laden der Immobilien.');
      }
      
      const matchedProperty = findPropertyByFuzzyName(propertyName, propertiesData.properties);
      
      if (!matchedProperty) {
        const notFoundMessage = {
          id: loadingMessage.id,
          type: 'assistant',
          content: `Ich konnte keine Immobilie mit dem Namen "${propertyName}" finden.\n\n${formatPropertyList(propertiesData.properties)}`,
          timestamp: new Date().toISOString(),
          isLoading: false
        };
        
        setMessages(prev => prev.map(msg => 
          msg.id === loadingMessage.id ? notFoundMessage : msg
        ));
        await saveMessage(notFoundMessage);
        return;
      }
      
      console.log('[BANKREPORT] Matched property:', matchedProperty.property_name);
      console.log('[BANKREPORT] Calling interactive API to generate presentation...');
      
      // Get current user ID from Supabase
      const { data: { user } } = await masterSupabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const reportResponse = await fetch('/api/reports/interactive/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          propertyName: matchedProperty.property_name,
          userId: user.id
        })
      });
      
      const reportData = await reportResponse.json();
      
      if (!reportData.success) {
        throw new Error(reportData.message || 'Fehler beim Erstellen des Reports.');
      }
      
      console.log('[BANKREPORT] Interactive report generated:', reportData.reportId);
      
      const reportMessage = {
        id: loadingMessage.id,
        type: 'assistant',
        content: `✅ **Interaktiver Bankreport erstellt!**\n\n📊 **${matchedProperty.property_name}**\n${matchedProperty.city}\n\n🎯 **Report-ID:** ${reportData.reportId}\n⏰ **Gültig bis:** ${new Date(reportData.expiresAt).toLocaleDateString('de-DE')}\n\n[📊 Interaktive Präsentation öffnen](${reportData.reportUrl})\n\nDie Präsentation enthält:\n• Finanzkennzahlen\n• Visualisierungen\n• KI-Insights\n• PDF-Export-Funktion\n• Teilbare Links`,
        reportId: reportData.reportId,
        reportUrl: reportData.reportUrl,
        timestamp: new Date().toISOString(),
        isLoading: false
      };
      
      setMessages(prev => prev.map(msg => 
        msg.id === loadingMessage.id ? reportMessage : msg
      ));
      await saveMessage(reportMessage);
      
      const summaryMatch = reportData.report.match(/^[^\n]+/);
      if (summaryMatch) {
        setTimeout(() => {
          speakText(summaryMatch[0]);
        }, 500);
      }
      
    } catch (error) {
      console.error('[BANKREPORT] Error:', error);
      
      const errorMessage = {
        id: loadingMessage.id,
        type: 'assistant',
        content: `❌ Fehler beim Erstellen des Bankreports:\n\n${error.message}`,
        timestamp: new Date().toISOString(),
        isLoading: false
      };
      
      setMessages(prev => prev.map(msg => 
        msg.id === loadingMessage.id ? errorMessage : msg
      ));
      await saveMessage(errorMessage);
    }
  }, [saveMessage, speakText]);

  // Handle sending messages - DUAL LAYER INTELLIGENCE (SUPABASE + GEMINI)
    const handleSendMessage = useCallback(async (message) => {
    if (inFlightRef.current) {
      console.log("[GUARD] Blocking duplicate handleSendMessage call - request already in flight");
      return;
    }
    
    // DISABLED: Frontend BankReport detection - now handled by backend /api/ask
    // This allows backend to show property selection buttons with conversation state
    /*
    const lowerMessage = message.toLowerCase();
    const isBankReportRequest = 
      lowerMessage.includes('bankreport') ||
      lowerMessage.includes('bank report') ||
      lowerMessage.includes('bankbericht') ||
      lowerMessage.includes('bank bericht') ||
      lowerMessage.includes('bericht erstellen') ||
      lowerMessage.includes('erstelle bericht') ||
      lowerMessage.includes('erstelle bankbericht') ||
      lowerMessage.includes('erstelle bankreport') ||
      lowerMessage.includes('report für') ||
      lowerMessage.includes('erstelle report') ||
      lowerMessage.includes('immobilienbericht') ||
      lowerMessage.includes('immobilienreport') ||
      lowerMessage.includes('finanzreport') ||
      lowerMessage.includes('finanzbericht');
    
    if (isBankReportRequest) {
      console.log('[BANKREPORT] Detected BankReport request, routing to handleBankReportRequest');
      await handleBankReportRequest(message);
      return;
    }
    */
    
    inFlightRef.current = true;
    setIsLoading(true);
    setInputValue("");

    const userMessage = {
      id: crypto.randomUUID(),
      type: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };

    // Add user message and a loading placeholder for the assistant's response
    const loadingMessage = {
      id: crypto.randomUUID(),
      type: 'assistant',
      content: '...',
      isLoading: true,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage, loadingMessage]);
    
    // Save user message to database
    await saveMessage(userMessage);

    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      const accessToken = session?.access_token;
      const idempotencyKey = crypto.randomUUID();

      let responseText;
      let data = null;

      // STREAMING: Local model uses SSE stream, cloud models use regular fetch
      const isLocalModel = false; // Qwen nutzt den gemeinsamen /api/ask-Fachkern; Streaming ohne Toolzyklus bleibt deaktiviert.
      
      if (isLocalModel) {
        // === STREAMING PATH for local Qwen32b ===
        const streamResponse = await fetch('/api/ask-stream', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Clara-Model': selectedModel,
            ...(accessToken && { "Authorization": `Bearer ${accessToken}`, "x-supabase-access-token": accessToken }),
          },
          body: JSON.stringify({ question: message, preferredLLM: selectedModel })
        });
        
        if (!streamResponse.ok) throw new Error(`Stream API error: ${streamResponse.status}`);
        
        const reader = streamResponse.body.getReader();
        const decoder = new TextDecoder();
        let streamedText = '';
        let routingMetadata = null;
        let voiceBuffer = ''; // accumulates tokens until flush condition

        // Robust sentence detection:
        // - matches .!? followed by whitespace OR at end of string
        // - avoids false triggers on abbreviations (z.B.) and numbers (1.500)
        const isSentenceEnd = (buf) => {
          // Must have .!? and next char is space/newline OR it's end of buffer
          return /[.!?](\s|$)/.test(buf) &&
            // Exclude: single uppercase letter before dot (abbreviation like "z.B.")
            !/[A-Za-z]\.$/.test(buf.trimEnd()) &&
            // Exclude: number before dot (decimal like "1.500")
            !/\d\.\d/.test(buf);
        };

        // Sentence-based TTS: speak each chunk via TTSService (Katja-Server)
        const speakSentence = (sentence) => {
          const clean = sentence.trim().replace(/[*_#`]/g, '');
          if (!clean) return;
          setIsSpeaking(true);
          TTSService.speak(clean).catch(() => {
            // Fallback auf Browser-TTS
            if ('speechSynthesis' in window && selectedVoice) {
              const utt = new SpeechSynthesisUtterance(clean);
              utt.voice = selectedVoice;
              utt.lang = 'de-DE';
              utt.rate = 1.1;
              utt.onend = () => { if (!speechSynthesis.pending) setIsSpeaking(false); };
              speechSynthesis.speak(utt);
            }
          });
        };

        // Flush voiceBuffer: find sentence boundaries and speak each segment
        const flushVoiceBuffer = (forceAll = false) => {
          // Try sentence-end split first
          const sentenceRegex = /([.!?])(\s+)/g;
          let lastIndex = 0;
          let match;
          while ((match = sentenceRegex.exec(voiceBuffer)) !== null) {
            const sentence = voiceBuffer.slice(lastIndex, match.index + 1);
            lastIndex = match.index + match[0].length;
            speakSentence(sentence);
          }
          voiceBuffer = voiceBuffer.slice(lastIndex);

          // Soft-streaming: if buffer has 15+ words and no sentence end found, speak it anyway
          const wordCount = voiceBuffer.trim().split(/\s+/).length;
          if (wordCount >= 15 || forceAll) {
            if (voiceBuffer.trim()) speakSentence(voiceBuffer);
            voiceBuffer = '';
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n').filter(l => l.startsWith('data: '));

          for (const line of lines) {
            try {
              const parsed = JSON.parse(line.slice(6));
              if (parsed.routing) routingMetadata = parsed.routing;
              if (parsed.artifact_reference) data = { ...(data || {}), artifact_reference: parsed.artifact_reference };
              if (parsed.error) throw new Error(parsed.error);
              if (parsed.token) {
                streamedText += parsed.token;
                voiceBuffer += parsed.token;
                // Live update: show tokens as they arrive
                setMessages(prev => prev.map(msg =>
                  msg.id === loadingMessage.id
                    ? { ...msg, content: streamedText, isLoading: false }
                    : msg
                ));
                // Flush complete sentences + soft-streaming trigger
                flushVoiceBuffer(false);
              }
              if (parsed.done) {
                responseText = parsed.fullText || streamedText;
                data = { answer_markdown: responseText, type: 'assistant', routing: routingMetadata, artifact_reference: data?.artifact_reference || null };
                // Speak any remaining buffer
                flushVoiceBuffer(true);
              }
            } catch(e) { /* skip malformed chunks */ }
          }
        }
        responseText = responseText || streamedText || '';
        // RESPONSE-RESILIENCE: Server-Sentinel sind niemals ein Antwortinhalt
        if (/^Keine Antwort( erhalten| verf\w+)?\.?$/.test(responseText)) responseText = '';
        responseText = responseText || 'Entschuldigung, es gab ein Problem bei der Antwort.';
        
      } else {
        // Operative Mieteingangsfragen werden vor der Dokumentrecherche an
        // die deterministische, organisationsgesicherte Capital-Capability geleitet.
        const isRentPaymentQuestion = /mieteingang|miete bezahlt|miete nicht bezahlt|zahlungsrückstand|mietrückstand|wer hat gezahlt|wer hat nicht gezahlt|offene mieten/i.test(String(message || ''));
        const normalizedQuestion = String(message || '').toLocaleLowerCase('de-DE');
        // CLARAKI-RENT-CONDITIONS-ROUTING-1: Operative Tabellenfragen sind
        // lesende Auskünfte und dürfen niemals in den UPDATE_RENT-Workflow fallen.
        const isRentConditionsQuestion = /(?:aktuell(?:e|en|er)?|derzeit(?:ig(?:e|en|er)?)|laufend(?:e|en|er)?)\s+(?:miete|mieten|nebenkosten|nk|betriebskosten)|(?:miete|mieten|nebenkosten|nk|betriebskosten).{0,90}(?:tabelle|übersicht|liste|einheit|darstellen)|(?:mieterhöhung|mieterhöhungen|neb(?:en)?kostenerhöhung|neb(?:en)?kostenerhöhungen).{0,90}(?:tabelle|übersicht|liste|einheit|darstellen)/i.test(String(message || ''));
        const namedProperty = (properties || []).find(property => {
          const name = String(property.property_name || '').toLocaleLowerCase('de-DE');
          return name.length >= 4 && normalizedQuestion.includes(name);
        });
        const rentArgs = {
          months: 1,
          status_filter: 'all',
          ...(namedProperty?.property_name ? { property_name: namedProperty.property_name } : {})
        };
        // CLARAKI-RENT-CONDITIONS-PROPERTY-REFERENCE-2: Wenn das Objekt nicht
        // in der Anzeigeauswahl steht, wird die gesamte Nutzerfrage nur an den
        // organisationsgescopten Capital-Resolver weitergegeben. Dieser akzeptiert
        // ausschließlich einen vollständigen, eindeutigen kanonischen Namen oder
        // eine vollständige kanonische Objektadresse; Mehrdeutigkeit bleibt gesperrt.
        const rentConditionsArgs = namedProperty?.id
          ? { property_id: namedProperty.id }
          : { property_reference: String(message || '').trim() };
        const capabilityId = isRentConditionsQuestion ? 'get_rent_conditions_table' : (isRentPaymentQuestion ? 'get_rent_payment_status' : null);
        const capabilityArgs = isRentConditionsQuestion ? rentConditionsArgs : rentArgs;
        // CLARA KI PAYER-FRAGE (2026-10-23, B+): Feste Auskunft aus echten Zahlungsdaten
        // (payment_identity_mapping + Ueberweisungen). Kein LLM-Freitext, sondern Tabelle.
        // ── Zahlungs-Historie: "wer hat wann wieviel bezahlt/gezahlt" (VOR payer pruefen) ──
        const msgHist = String(message || '');
        const isHistoryQuestion = /(wer|welche[rm]?)\s+hat\s+.*(gezahlt|bezahlt)|zahlungshistorie|zahlungsverlauf|wer\s+.*gezahlt|miete\s+bezahlt|gesamtmiete[n]?\s+bezahlt|im\s+zeitraum|von\s+\d{2}\.\d{2}\.|20\d{2}\s+.*bezahlt/i.test(msgHist)
          && /(gezahlt|bezahlt|zahlung|historie|verlauf)/i.test(msgHist);
        // ── NK-Zahlungsanalyse: "Wie viele NK bezahlt?" (VOR History prüfen) ──
        const isNkQuestion = /((wie\s+viel\w*|welche)\s+)?(nebenkosten|nk)\b[^.]{0,40}(bezahlt|gezahlt|eingegangen)|nebenkosten.{0,20}(analyse|abgleich|differenz)/i.test(msgHist)
          && /(bezahlt|gezahlt|eingegangen|analyse|differenz)/i.test(msgHist);
        if (isNkQuestion && !isHistoryQuestion) {
          try {
            const { data: { session: nkSession } } = await masterSupabase.auth.getSession();
            const nkToken = nkSession?.access_token;
            const nkObj = msgHist.match(/\b(WHS|NAS|BMF|HAP|P7,?1|Drais\w*|Scheffel\w*|Hauptstr\w*|Mainzer\w*|Waldhof\w*|Neckarauer\w*|Hafenpark\w*|Bürgermeister\w*|Buergermeister\w*)/i);
            const nkJahr = msgHist.match(/\b(20\d{2})\b/);
            const nkParams = new URLSearchParams();
            if (nkObj) nkParams.set('property', nkObj[1]);
            if (nkJahr) nkParams.set('year', nkJahr[1]);
            const nkResp = await fetch('/api/rent/nk-payment-analysis?' + nkParams.toString(), {
              headers: nkToken ? { 'Authorization': 'Bearer ' + nkToken } : {}
            });
            const nkData = await nkResp.json();
            if (nkResp.ok && nkData?.ok && Array.isArray(nkData.mieter)) {
              const nkF = `Objekt: ${nkData.filter?.property || 'alle'} | Jahr: ${nkData.filter?.year || 'alle'}`;
              let tab = `**NK-Zahlungsanalyse** (${nkF})\n\n`;
              tab += 'Regel: Kaltmiete zuerst, Rest = NK. NK-Erhöhungen: ' + (nkData.nk_erhoehungen || 'nicht erfasst') + '.\n\n';
              tab += '| Mieter | Einheit | Soll-NK | bezahlt | davon NK | NK-Differenz |\n';
              tab += '|---|---|---|---|---|---|\n';
              for (const m of nkData.mieter.slice(0, 20)) {
                tab += `| ${m.mieter} | ${m.einheit || '—'} | ${m.soll_nk.toFixed(2)} € | ${m.bezahlt.toFixed(2)} € | ${m.davon_nk.toFixed(2)} € | ${m.nk_differenz >= 0 ? '+' : ''}${m.nk_differenz.toFixed(2)} € |\n`;
              }
              const nkMsg = { role: 'assistant', content: tab, timestamp: new Date().toISOString() };
              setMessages(prev => [...prev, nkMsg]);
              if (typeof saveMessage === 'function') saveMessage(nkMsg);
              return;
            }
            const nkNa = { role: 'assistant', content: 'Auskunft derzeit nicht verfügbar. Bitte versuchen Sie es erneut.', timestamp: new Date().toISOString() };
            setMessages(prev => [...prev, nkNa]);
            if (typeof saveMessage === 'function') saveMessage(nkNa);
            return;
          } catch (errNk) {
            const nkNa2 = { role: 'assistant', content: 'Auskunft derzeit nicht verfügbar. Bitte versuchen Sie es erneut.', timestamp: new Date().toISOString() };
            setMessages(prev => [...prev, nkNa2]);
            if (typeof saveMessage === 'function') saveMessage(nkNa2);
            return;
          }
        }
        if (isHistoryQuestion) {
          try {
            const { data: { session: hSession } } = await masterSupabase.auth.getSession();
            const hToken = hSession?.access_token;
            // Filter aus der Frage extrahieren: Objekt + Jahr
            const objMatch = msgHist.match(/\b(WHS|NAS|BMF|HAP|P7,?1|Drais\w*|Scheffel\w*|Hauptstr\w*|Mainzer\w*|Waldhof\w*|Neckarauer\w*|Buergermeister\w*|Bürgermeister\w*|Hafenpark|Industriestr\w*)\b/i);
            const jahrMatch = msgHist.match(/\b(20\d{2})\b/);
            const params = new URLSearchParams();
            if (objMatch) params.set('property', objMatch[1]);
            if (jahrMatch) params.set('year', jahrMatch[1]);
            const hResp = await fetch('/api/rent/payment-history?' + params.toString(), {
              headers: hToken ? { 'Authorization': 'Bearer ' + hToken } : {}
            });
            const hData = await hResp.json();
            if (hResp.ok && hData?.ok && Array.isArray(hData.mieter)) {
              const filterH = `Objekt: ${hData.filter?.property || 'alle'} | Zeitraum: ${hData.filter?.year || 'alle'}`;
              let hist = `**Zahlungs-Historie** (${filterH})\n\n`;
              hist += '| Mieter | Anzahl | Gesamt | Miete | NK | Zeitraum |\n';
              hist += '|---|---|---|---|---|---|\n';
              for (const m of hData.mieter.slice(0, 20)) {
                const zs = m.zahlungen || [];
                const von = zs.length ? String(zs[0].datum).slice(0,10) : '—';
                const bis = zs.length ? String(zs[zs.length-1].datum).slice(0,10) : '—';
                hist += `| ${m.mieter} | ${m.anzahl}× | ${m.summe.toFixed(2)} € | ${m.kaltmiete.toFixed(2)} € | ${m.nebenkosten.toFixed(2)} € | ${von} – ${bis} |\n`;
              }
              const msg = { role: 'assistant', content: hist, timestamp: new Date().toISOString() };
              setMessages(prev => [...prev, msg]);
              if (typeof saveMessage === 'function') saveMessage(msg);
              return;
            }
            // Kein Ergebnis: klare Meldung – NIE die Payer-Tabelle zeigen
            const naMsg1 = { role: 'assistant', content: 'Auskunft derzeit nicht verfügbar. Bitte versuchen Sie es erneut.', timestamp: new Date().toISOString() };
            setMessages(prev => [...prev, naMsg1]);
            if (typeof saveMessage === 'function') saveMessage(naMsg1);
            return;
          } catch (errHist) {
            const naMsg2 = { role: 'assistant', content: 'Auskunft derzeit nicht verfügbar. Bitte versuchen Sie es erneut.', timestamp: new Date().toISOString() };
            setMessages(prev => [...prev, naMsg2]);
            if (typeof saveMessage === 'function') saveMessage(naMsg2);
            return;
          }
        }
        const isPayerQuestion = /jobcenter|bundesagentur|arbeitsagentur|bundesamt\s+f(ü|u)r\s+arbeit|zahlungsquelle|zahler|wer\s+zahlt|zahlt\s+(die|der)?\s*miete|miete\s+von|mieterhöh|mieterhoeh|erhöh|erhoeh|erhoe|mieterwechsel|neuvermietung|kuendig|kündigung|zahlung|gezahlt|miete|nebenkosten|nk\b|kaution/i.test(String(message || ''));
        if (isPayerQuestion) {
          try {
            // Token-Weg IDENTISCH zum funktionierenden Mieter-Sync (propertiesService.syncTenantToUnit)
            const { data: { session: pSession } } = await masterSupabase.auth.getSession();
            const pToken = pSession?.access_token;
            if (!pToken) {
              const msg = { id: `payer-${Date.now()}`, type: 'assistant', content: 'Bitte erneut anmelden, um die Zahlungsquellen-Auskunft zu laden.', isUser: false, timestamp: new Date().toISOString() };
              setMessages(prev => [...prev, msg]);
              if (typeof saveMessage === 'function') saveMessage(msg);
              return;
            }
            const payerResp = await fetch('/api/rent/payer-overview', {
              headers: { 'Authorization': 'Bearer ' + pToken },
            });
            if (!payerResp.ok) {
              const code = payerResp.status;
              const hinweis = (code === 401 || code === 403)
                ? 'Keine Berechtigung fuer die Zahlungsquellen-Auskunft (Rolle erforderlich). Bitte ggf. erneut anmelden.'
                : `Auskunft derzeit nicht verfuegbar (HTTP ${code}).`;
              const msg = { id: `payer-${Date.now()}`, type: 'assistant', content: hinweis, isUser: false, timestamp: new Date().toISOString() };
              setMessages(prev => [...prev, msg]);
              if (typeof saveMessage === 'function') saveMessage(msg);
              return;
            }
            const payerData = await payerResp.json();
            const allRows = (payerData && payerData.rows) || [];
            const agencyRows = allRows.filter(r => r.payer_type === 'agency' || /bundesagentur|jobcenter|arbeitsagentur|bundesamt/i.test(String(r.payer_name || '')));
            const ausgabe = agencyRows.length ? agencyRows : allRows;
            let tabelle = 'Wer zahlt die Miete? (aus echten Zahlungsdaten)\n\n';
            if (!ausgabe.length) {
              tabelle += 'Keine Zahlungsquellen-Zuordnung gefunden.';
            } else {
              tabelle += '| Mieter | Einheit | Zahler | Typ | Status | Miete (Kalt/NK/Gesamt) | Flaeche | Kaltmiete/m² | Letzte Ueberweisung | Mieterhoehung |\n';
              tabelle += '|---|---|---|---|---|---|---|---|---|---|\n';
              const eur = (v) => (v == null ? '—' : `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
              for (const r of ausgabe) {
                const lz = r.letzte_zahlung ? `${String(r.letzte_zahlung).slice(0, 10)}` : '—';
                const n = (r.n_zahlungen ? ` · ${r.n_zahlungen}×` : '');
                const typ = r.payer_type === 'agency' ? 'Amt' : (r.payer_type === 'direct' ? 'Selbst' : 'Dritter');
                const ort = r.property_name || r.unit_identifier || '—';
                const gemischt = r.gemischt ? ' (gemischt)' : '';
                const miete = `${eur(r.kaltmiete)} / ${eur(r.nebenkosten)} / ${eur(r.gesamtmiete)}`;
                const fl = r.flaeche ? `${Number(r.flaeche).toLocaleString('de-DE')} m²` : '—';
                const pm2 = r.kaltmiete_pro_m2 != null ? `${Number(r.kaltmiete_pro_m2).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/m²` : '—';
                let erhText = 'keine Erhoehung dokumentiert';
                if (r.erhoehung_beleg) {
                  const b = r.erhoehung_beleg;
                  const bd = b.datum ? String(b.datum).slice(0, 10) : '—';
                  erhText = `Ja – am ${bd} (Beleg: ${String(b.titel || 'Vault/paperless').slice(0, 42)})`;
                }
                if (r.wechsel && !r.erhoehung_beleg) { erhText = 'Mieterwechsel (keine Erhoehung)'; }
                else if (r.erhoehung && !r.erhoehung_beleg) {
                  const e = r.erhoehung;
                  erhText = `Ja – ab ${String(e.ab).slice(0, 10)}: ${eur(e.von)} → ${eur(e.auf)} (+${eur(e.delta)})`;
                }
                const status = r.status_anzeige || '—';
                let mieteAnzeige = miete;
                if (r.kaltmiete == null && r.miete_abgeleitet != null) {
                  mieteAnzeige = `${eur(r.miete_abgeleitet)} (abgeleitet) / — / —`;
                }
                tabelle += `| ${r.tenant_name} | ${ort} | ${r.payer_name}${gemischt} | ${typ} | ${status} | ${mieteAnzeige} | ${fl} | ${pm2} | ${lz}${n} | ${erhText} |\n`;
              }
            }
            // Mieterhoehungs-Historie (auch ohne Zuordnung) anhaengen
            const histAktiv = (payerData && Array.isArray(payerData.mieterhoehungen_aktiv)) ? payerData.mieterhoehungen_aktiv : [];
            const hist = (payerData && Array.isArray(payerData.mieterhoehungen_historie)) ? payerData.mieterhoehungen_historie : [];
            if (histAktiv.length) {
              tabelle += `\n**Mieterhöhungen – AKTIVE Mieter (relevant):**\n`;
              for (const h of histAktiv.slice(0, 12)) {
                const hd = h.datum ? String(h.datum).slice(0, 10) : '—';
                tabelle += `• ${hd} – ${h.name ? String(h.name).slice(0, 26) + ': ' : ''}${String(h.titel || '').slice(0, 56)}\n`;
              }
            }
            if (hist.length) {
              tabelle += `\n**Mieterhöhungen – Archiv (ausgeschiedene Mieter, ${hist.length} Belege):**\n`;
              for (const h of hist.slice(0, 10)) {
                const hd = h.datum ? String(h.datum).slice(0, 10) : '—';
                tabelle += `• ${hd} – ${h.name ? String(h.name).slice(0, 26) + ': ' : ''}${String(h.titel || '').slice(0, 52)}\n`;
              }
              if (hist.length > 10) tabelle += `… und ${hist.length - 10} weitere (Archiv)\n`;
            }
            const payerMessage = {
              id: `payer-${Date.now()}`,
              type: 'assistant',
              content: tabelle,
              isUser: false,
              timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, payerMessage]);
            if (typeof saveMessage === 'function') saveMessage(payerMessage);
          } catch (err) {
            console.error('[PAYER-FRAGE] Fehler:', err);
          }
          return;
        }
        const response = await fetch(capabilityId ? '/api/capabilities/prepare' : '/api/ask', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Clara-Model': selectedModel,
            'X-Idempotency-Key': idempotencyKey,
            ...(accessToken && { "Authorization": `Bearer ${accessToken}`, "x-supabase-access-token": accessToken }),
          },
          body: JSON.stringify(capabilityId
            ? { capability: capabilityId, args: capabilityArgs }
            : { question: message, preferredLLM: selectedModel })
        });
        
        if (response.ok) {
          data = await response.json();
          responseText = (data.message ?? '').trim() ||
                        (data.answer ?? '').trim() ||
                        (data.answer_markdown ?? '').trim() ||
                        (data.response ?? '').trim() ||
                        (data.content ?? '').trim() ||
                        '';
          // RESPONSE-RESILIENCE: Server-Sentinel sind niemals ein Antwortinhalt
          if (/^Keine Antwort( erhalten| verf\w+)?\.?$/.test(responseText)) responseText = '';
          responseText = responseText || 'Entschuldigung, es gab ein Problem bei der Antwort.';
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = response.status === 401
            ? 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.'
            : (errorData?.message || errorData?.error || `Die Anfrage konnte nicht verarbeitet werden (HTTP ${response.status}).`);
          throw new Error(errorMessage);
        }
      }

      // 🔥 USE NORMALIZEMESSAGE FOR CONSISTENT DATA SHAPE
      const assistantMessage = normalizeMessage({
        ...data,
        message: responseText,
        id: loadingMessage.id
      });
      
      // Preserve the loadingMessage ID to replace placeholder
      assistantMessage.id = loadingMessage.id;
      if (data?.routing) assistantMessage.routing = data.routing;
      if (data?.artifact_reference) assistantMessage.artifact_reference = data.artifact_reference;

      setMessages(prev => {
      const updated = prev.map(msg => msg.id === loadingMessage.id ? assistantMessage : msg);
      return [...updated]; // Force new array reference for React re-render
    });
      
      // Save assistant message to database
      await saveMessage(assistantMessage);

      // Unified voice pipeline via TTSService + VoiceFormatter
      // Cloud models: speak full response through VoiceFormatter
      if (selectedModel !== 'local' && responseText) {
        const { spokenText } = formatForVoice(responseText, {
          mode: detectResponseMode(message || ''),
          isMobile,
          isVoice: true,
        });
        setIsSpeaking(true);
        TTSService.speak(spokenText)
          .then(() => setIsSpeaking(false))
          .catch(() => setIsSpeaking(false));
      }

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: loadingMessage.id, // Use the same ID
        type: 'assistant',
        content: error?.message || 'Entschuldigung, es gab einen Fehler. Bitte versuchen Sie es erneut.',
        timestamp: new Date().toISOString(),
        isLoading: false,
      };
      setMessages(prev => prev.map(msg => msg.id === loadingMessage.id ? errorMessage : msg));
    } finally {
      setIsLoading(false);
      inFlightRef.current = false;
    }
  }, [user, speakText, selectedModel, saveMessage]);

  // Handle voice input with working hook
  const handleVoiceInput = useCallback(async () => {
    if (isListening) {
      stopListening();
      return;
    }

    try {
      await startListening();
    } catch (error) {
      console.error('Voice recognition error:', error);
    }
  }, [isListening, startListening, stopListening]);

  // Navigation Guard - prevent re-execution after navigation
  const lastNavigationRef = useRef(Date.now());
  
  useEffect(() => {
    // Update navigation timestamp when component mounts/remounts
    lastNavigationRef.current = Date.now();
    
    // Clear input and reset guards on navigation
    setInputValue('');
    inFlightRef.current = false;
  }, []);

  // Auto-populate input field with transcript for user review
  useEffect(() => {
    if (transcript && transcript.trim()) {
      console.log("[VOICE-DEBUG] Updating input field with transcript:", transcript.trim());
      setInputValue(transcript.trim());
    }
  }, [transcript]);

  // Action confirmation handlers
  const handleActionConfirm = useCallback(async (message) => {
    try {
      setIsLoading(true);
      const { data: { session } } = await masterSupabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error('Authentifizierung erforderlich');

      if (message?.approvalId) {
        const response = await fetch('/api/capabilities/execute', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-supabase-access-token': accessToken },
          body: JSON.stringify({ approval_id: message.approvalId, proposal_hash: message.proposalHash })
        });
        const result = await response.json();
        const normalized = normalizeMessage(result);
        setMessages(prev => [...prev, normalized]);
        await saveMessage(normalized);
        return;
      }

      // Übergangspfad für historische Bestätigungen außerhalb des Command Centers.
      const response = await fetch('/api/confirm-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-supabase-access-token': accessToken },
        body: JSON.stringify({ action: message?.action, conversationId: message?.conversationId, confirmed: true })
      });
      const normalized = normalizeMessage(await response.json());
      setMessages(prev => [...prev, normalized]);
      await saveMessage(normalized);
    } catch (error) {
      console.error('Action confirmation error:', error);
      setMessages(prev => [...prev, { id: Date.now(), type: 'assistant', content: `Ausführung nicht möglich: ${error.message}`, timestamp: new Date().toISOString() }]);
    } finally {
      setIsLoading(false);
    }
  }, [saveMessage]);
  
  const handleActionCancel = useCallback(() => {
    const cancelMessage = {
      id: Date.now(),
      type: 'assistant',
      content: 'Aktion wurde abgebrochen. Kann ich Ihnen anderweitig helfen?',
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, cancelMessage]);
  }, []);
  
  // Workflow handlers
  const handleWorkflowSubmit = useCallback(async ({ capability, args }, messageId) => {
    try {
      setIsLoading(true);
      const { data: { session } } = await masterSupabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error('Authentifizierung erforderlich');
      const response = await fetch('/api/capabilities/prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-supabase-access-token': accessToken },
        body: JSON.stringify({ capability, args })
      });
      const result = await response.json();
      const normalized = normalizeMessage(result);
      setMessages(prev => [...prev, normalized]);
      await saveMessage(normalized);
    } catch (err) {
      console.error('[CapabilityForm] Fehler:', err);
      setMessages(prev => [...prev, { id: Date.now(), type: 'assistant', content: `Anfrage nicht möglich: ${err.message}`, timestamp: new Date().toISOString() }]);
    } finally {
      setIsLoading(false);
    }
  }, [saveMessage]);

  
  const handleWorkflowConfirm = useCallback(async (confirmation) => {
    try {
      setIsLoading(true);
      
      // Get current session for JWT token
      const { data: { session } } = await masterSupabase.auth.getSession();
      const accessToken = session?.access_token;
      
      const response = await fetch('/api/execute-workflow', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-supabase-access-token': accessToken || 'fallback-token'
        },
        body: JSON.stringify(confirmation)
      });
      
      const result = await response.json();
      
      const confirmationMessage = {
        id: Date.now(),
        type: 'assistant',
        content: result.success ? result.message : `Fehler: ${result.error}`,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, confirmationMessage]);
      
    } catch (error) {
      console.error('Workflow confirmation error:', error);
      const errorMessage = {
        id: Date.now(),
        type: 'assistant',
        content: 'Entschuldigung, es gab einen Fehler bei der Ausführung des Workflows.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Property Selection Handler for BankReport
  const handlePropertySelect = useCallback(async (property) => {
    console.log('[PROPERTY_SELECT] Property selected:', property.name);
    
    try {
      setIsLoading(true);
      
      // Create user message showing selection
      const userMessage = {
        id: crypto.randomUUID(),
        type: 'user',
        content: `Bankreport für ${property.name}`,
        timestamp: new Date().toISOString()
      };
      
      const loadingMessage = {
        id: crypto.randomUUID(),
        type: 'assistant',
        content: '🏦 Erstelle Bankreport...',
        isLoading: true,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, userMessage, loadingMessage]);
      await saveMessage(userMessage);
      
      // Get current user for authentication
      const { data: { user } } = await masterSupabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Call BankReport API directly
      const reportResponse = await fetch('/api/reports/interactive/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          propertyName: property.name,
          propertyId: property.id,
          userId: user.id
        })
      });
      
      const reportData = await reportResponse.json();
      
      if (!reportData.success) {
        throw new Error(reportData.message || 'Fehler beim Erstellen des Reports.');
      }
      
      console.log('[PROPERTY_SELECT] Report generated:', reportData.reportId);
      
      // Create success message with report link
      const reportMessage = {
        id: loadingMessage.id,
        type: 'assistant',
        content: `✅ **Interaktiver Bankreport erstellt!**\n\n📊 **${property.name}**\n${property.location}\n\n🎯 **Report-ID:** ${reportData.reportId}\n⏰ **Gültig bis:** ${new Date(reportData.expiresAt).toLocaleDateString('de-DE')}\n\n[📊 Interaktive Präsentation öffnen](${reportData.reportUrl})\n\nDie Präsentation enthält:\n• Finanzkennzahlen\n• Visualisierungen\n• KI-Insights\n• PDF-Export-Funktion\n• Teilbare Links`,
        reportId: reportData.reportId,
        reportUrl: reportData.reportUrl,
        timestamp: new Date().toISOString(),
        isLoading: false
      };
      
      setMessages(prev => prev.map(msg => 
        msg.id === loadingMessage.id ? reportMessage : msg
      ));
      await saveMessage(reportMessage);
      
      // Speak summary
      const summaryMatch = reportData.report?.match(/^[^\n]+/);
      if (summaryMatch) {
        setTimeout(() => {
          speakText(summaryMatch[0]);
        }, 500);
      }
      
    } catch (error) {
      console.error('[PROPERTY_SELECT] Error:', error);
      
      const errorMessage = {
        id: Date.now(),
        type: 'assistant',
        content: `❌ Fehler beim Erstellen des Bankreports:\n\n${error.message}`,
        timestamp: new Date().toISOString(),
        isLoading: false
      };
      
      setMessages(prev => [...prev, errorMessage]);
      await saveMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [saveMessage, speakText]);

  // orchestrateDocument: Dokumenttyp erkennen + Felder fuer Workflow-Formulare extrahieren
  // (vorher nie implementiert -> ReferenceError). Reine Logik, keine Buchung/Kommunikation.
  async function orchestrateDocument(doc) {
    const ocr = doc?.ocr_data || {};
    const text = [
      doc?.detected_type || '',
      doc?.extracted_text || '',
      ocr.summary || '', ocr.document_type || '', ocr.text || '', ocr.extracted_text || '',
      doc?.name || ''
    ].join(' ').toLowerCase();

    const grab = (patterns) => {
      for (const re of patterns) {
        const m = text.match(re);
        if (m && m[1]) return m[1].trim();
      }
      return '';
    };

    // Mietvertrag erkennen
    if (/mietvertrag|mietvertragsurkunde|wohnraummietvertrag|lease agreement/.test(text)) {
      const tenantName = grab([
        /(?:mieter|pächter)[^a-z0-9]{0,10}(?:herrn?|frau)?\s+([a-zäöüß\-]+(?:\s+[a-zäöüß\-]+){1,2})/,
        /(?:herrn?|frau)\s+([a-zäöüß\-]+(?:\s+[a-zäöüß\-]+){1,2})/
      ]);
      const coldRent = grab([
        /kaltmiete[^0-9]{0,25}([0-9]{1,4}(?:[.,][0-9]{2})?)/,
        /nettomiete[^0-9]{0,25}([0-9]{1,4}(?:[.,][0-9]{2})?)/
      ]);
      const nk = grab([
        /nebenkosten(?:vorauszahlung)?[^0-9]{0,25}([0-9]{1,4}(?:[.,][0-9]{2})?)/
      ]);
      const begin = grab([
        /(?:mietbeginn|beginn des mietverhältnisses|ab dem?)\s*(\d{1,2}\.\s*\w+\s*\d{4})/,
        /(?:mietbeginn|beginn)[^0-9]{0,15}(\d{1,2}\.\d{1,2}\.\d{4})/
      ]);
      return {
        type: 'workflow_form',
        workflow: 'mieter_anlage',
        title: 'Mietvertrag erkannt – Mieter anlegen',
        description: 'Aus dem Mietvertrag extrahierte Angaben bitte pruefen und das Formular bestaetigen.',
        fields: [
          { name: 'mieter_name', label: 'Mietername', type: 'text', defaultValue: tenantName, required: true },
          { name: 'kaltmiete', label: 'Kaltmiete (EUR)', type: 'text', defaultValue: coldRent },
          { name: 'nebenkosten', label: 'Nebenkosten (EUR)', type: 'text', defaultValue: nk },
          { name: 'mietbeginn', label: 'Mietbeginn', type: 'text', defaultValue: begin }
        ]
      };
    }

    // Unbekannter Typ: neutral weitergeben, nie crashen
    return {
      type: 'document_summary',
      summary: doc?.extracted_text || 'Dokument gespeichert – kein bekannter Workflow-Typ erkannt.'
    };
  }
  // NEW: Document Upload Handler with Orchestration
  const handleDocumentUpload = useCallback(async (data) => {
    console.log('[UPLOAD] Document uploaded successfully:', data);
    
    try {
      // 1. Map upload data to orchestrator format
      const docForOrchestration = {
        detected_type: data.document?.ai_parsed_data?.document_type || data.document?.document_type || 'unknown',
        extracted_text: data.document?.ai_parsed_data?.summary || '',
        ocr_data: data.document?.ai_parsed_data || {},
        name: data.document?.filename || data.document?.name || 'unknown',
        id: data.document?.id || Date.now().toString()
      };
      
      console.log('[UPLOAD] Mapped document for orchestration:', docForOrchestration);
      
      // 2. Orchestriere das Dokument (erkennt Typ und extrahiert Daten)
      const orchestration = await orchestrateDocument(docForOrchestration);
      console.log('[ORCHESTRATE] Result:', orchestration);
      
      // 2. Wenn Workflow erkannt wurde → Trigger Workflow
      if (orchestration?.type === 'workflow_form') {
        console.log('[ORCHESTRATE] Triggering workflow:', orchestration.workflow);
        
        // System-Nachricht
        const systemMessage = {
          id: `system-${Date.now()}`,
          type: 'system',
          content: `📄 Dokument "${data.document?.name}" erkannt als ${orchestration.workflow}. Öffne Formular mit extrahierten Daten...`,
          isUser: false,
          timestamp: new Date().toISOString()
        };
        
        // Workflow Form Message mit extrahierten Daten
        const workflowMessage = {
          id: `workflow-${Date.now()}`,
          type: 'workflow_form',
          content: `Formular für ${orchestration.workflow}`,
          isUser: false,
          timestamp: new Date().toISOString(),
          workflow: {
            name: orchestration.workflow,
            fields: orchestration.fields || [],
            description: orchestration.description,
            title: orchestration.title,
            data: orchestration.data || {}  // 🔥 PREFILL DATA FROM EXTRACTION
          }
        };
        
        setMessages(prev => [...prev, systemMessage, workflowMessage]);
        saveMessage(systemMessage);
        saveMessage(workflowMessage);
      } else {
        // Fallback: Zeige Success-Nachricht
        const documentContext = {
          id: data.document?.id,
          name: data.document?.name || 'Unbekannt',
          type: data.document?.document_type || 'document',
          summary: data.document?.ai_summary || '',
          uploadedAt: new Date().toISOString()
        };
        
        const successMessage = {
          id: Date.now() + 1,
          type: 'assistant',
          content: `✅ Dokument "${documentContext.name}" wurde erfolgreich hochgeladen und analysiert!\n\nDu kannst mich jetzt nach diesem Dokument fragen.`,
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, successMessage]);
        saveMessage(successMessage);
      }
    } catch (error) {
      console.error('[UPLOAD] Error during orchestration:', error);
      
      // Error message
      const errorMessage = {
        id: Date.now(),
        type: 'assistant',
        content: `❌ Fehler bei der Verarbeitung des Dokuments: ${error.message}`,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      saveMessage(errorMessage);
    }
    // Punkt 2 (Clara KI, 2026-10-23): Frage aus dem Eingabefeld nach dem Dokument-Upload
    // automatisch mitsenden, damit sie nicht verloren geht (sonst antwortet Clara nur
    // generisch auf den Upload).
    // Upload-Nachricht (2026-10-23 v2): IMMER eine deutsche Nachricht senden.
    // - Wenn eine Frage im Eingabefeld steht: die Frage + Sprachhinweis.
    // - Wenn nicht: Standard-Anweisung, damit nie wieder "no specific request" kommt.
    const pendingQuestion = (typeof inputValue === 'string' && inputValue.trim()) ? inputValue.trim() : '';
    if (pendingQuestion) setInputValue('');
    const sprachHinweis = '\n\n(Bitte antworte immer auf Deutsch.)';
    const uploadFrage = pendingQuestion
      ? pendingQuestion + sprachHinweis
      : 'Analysiere das gerade hochgeladene Dokument auf Deutsch. Wenn es Mieter- oder Zahlungsdaten enthaelt: Liste Mieter, Objekte, Einheiten und Zahlungsquellen (z. B. Bundesagentur, JobCenter) uebersichtlich auf.' + sprachHinweis;
    handleSendMessage(uploadFrage);
  }, [saveMessage, inputValue, handleSendMessage]);

  // Supabase status helpers
  const getSupabaseStatusColor = () => {
    switch (supabaseStatus) {
      case 'connected': return 'text-blue-400';
      case 'connecting': return 'text-yellow-400';
      case 'error': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getSupabaseStatusText = () => {
    switch (supabaseStatus) {
      case 'connected': return 'Datenbank verbunden';
      case 'connecting': return 'Verbinde...';
      case 'error': return 'Verbindungsfehler';
      default: return 'Unbekannt';
    }
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {activeArtifact && (activeArtifact.source_kind === 'supabase_document'
        ? <SupabaseDocumentModal documentSource={activeArtifact} onClose={() => setActiveArtifact(null)} />
        : <VaultArtifactModal artifactReference={activeArtifact} onClose={() => setActiveArtifact(null)} />
      )}
      
      {/* Subtle Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/3 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-600/3 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
      </div>

      {/* Header with subtle styling */}
      <div className="relative bg-gradient-to-r from-gray-800/60 to-slate-800/60 backdrop-blur-sm border-b border-gray-700/30">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative p-3 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg">
                <Bot className="w-8 h-8 text-white" />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl opacity-30 animate-pulse" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Clara KI</h1>
                <p className="text-gray-300 text-sm">Capital Truth + Vault Evidence</p>
              </div>
            </div>
            
            {/* Status Indicators */}
            <div className="flex items-center space-x-4">
              {/* Clear Chat Button */}
              <button
                onClick={clearChat}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-lg shadow-red-600/25 flex items-center space-x-2"
                title="Chat-Verlauf löschen"
              >
                <Trash2 className="w-4 h-4" />
                <span>Chat löschen</span>
              </button>
              
              {/* Roadmap Button - SUPER_ADMIN only */}
              {isSuperAdmin && (
                <a
                  href="/clara-ki-roadmap"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-lg shadow-purple-600/25 flex items-center space-x-2"
                  title="Clara KI Roadmap anzeigen"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Roadmap</span>
                </a>
              )}
              
              {/* NEW: LLM Model Toggle */}
              <LLMToggle 
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
              />
              
              <div className="flex items-center space-x-2">
                <Database className={`w-4 h-4 ${getSupabaseStatusColor()}`} />
                <span className={`text-sm ${getSupabaseStatusColor()}`}>
                  {getSupabaseStatusText()}
                </span>
              </div>
              
              {/* NEW: Portfolio Status */}
              {portfolioLoading && (
                <div className="flex items-center space-x-2">
                  <Database className="w-4 h-4 text-blue-400 animate-pulse" />
                  <span className="text-sm text-blue-400">Portfolio lädt...</span>
                </div>
              )}
              
              {portfolioError && (
                <div className="flex items-center space-x-2">
                  <Database className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-400">Portfolio-Fehler</span>
                </div>
              )}
              
              {portfolioData && portfolioStats && (
                <div className="flex items-center space-x-2">
                  <Database className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400">
                    Portfolio: {portfolioStats.totalProperties} Objekte, {portfolioStats.totalTenants} Mieter
                  </span>
                </div>
              )}
              
              {/* Voice Status */}
              {isListening && (
                <div className="flex items-center space-x-2">
                  <Mic className="w-4 h-4 text-red-400 animate-pulse" />
                  <span className="text-sm text-red-400">Hört zu...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div className="relative flex-1 flex flex-col h-[calc(100vh-120px)]">
        
        {/* Messages Area */}
        <div 
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto px-4 py-6 space-y-4"
        >
          {messages.map((message, index) => (
            <ChatMessage
              key={message.id}
              message={message}
              isUser={message.type === 'user' || message.is_from_me === 1}
              isLastClaraMessage={isLastClaraMessage(index)}
              onSpeak={speakText}
              onStopSpeaking={stopSpeaking}
              isSpeaking={isSpeaking}
              onActionConfirm={handleActionConfirm}
              onActionCancel={handleActionCancel}
              onWorkflowSubmit={handleWorkflowSubmit}
              onWorkflowConfirm={handleWorkflowConfirm}
              onPropertySelect={handlePropertySelect}
              onOpenArtifact={setActiveArtifact}
              properties={properties}
              units={units}
              portfolioData={portfolioData}
            />
          ))}
          
          {isLoading && <EnhancedLoading />}
        </div>

      {/* CLARA-COMMAND-CENTER-TRUTH-1: deterministischer Capability-Einstieg */}
      <CommandCenter
        disabled={isLoading}
        onTrigger={async (actionConfig) => {
          try {
            const { data: { session } } = await masterSupabase.auth.getSession();
            const token = session?.access_token;
            if (!token) throw new Error('Authentifizierung erforderlich');
            const systemMessage = {
              id: `system-${Date.now()}`,
              type: 'system',
              content: `Ich öffne „${actionConfig.label}“ für Sie...`,
              isUser: false,
              timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, systemMessage]);
            await saveMessage(systemMessage);
            const response = await fetch('/api/capabilities/prepare', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-supabase-access-token': token },
              body: JSON.stringify({ capability: actionConfig.capability, args: {} })
            });
            const result = await response.json();
            const normalized = normalizeMessage(result);
            setMessages(prev => [...prev, normalized]);
            await saveMessage(normalized);
          } catch (error) {
            console.error('[CommandCenter] Capability error:', error);
            const errorMessage = { id: `error-${Date.now()}`, type: 'system', content: `Funktion nicht verfügbar: ${error.message}`, isUser: false, timestamp: new Date().toISOString() };
            setMessages(prev => [...prev, errorMessage]);
            await saveMessage(errorMessage);
          }
        }}
      />

        {/* Input Area */}
        <div className="relative bg-gradient-to-r from-gray-800/60 to-slate-800/60 backdrop-blur-sm border-t border-gray-700/30 p-4">
          <div className="container mx-auto max-w-4xl">
            
            {/* Voice Error Display */}
            {voiceError && (
              <div className="mb-3 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm">🚨 {voiceError}</p>
              </div>
            )}
            
            
            <form 
              ref={formRef}
              onSubmit={(e) => {
                e.preventDefault();
                if (!inFlightRef.current && inputValue.trim()) {
                  console.log("[FORM] Single submit triggered");
                  handleSendMessage(inputValue.trim());
                }
              }}
              className="flex items-end space-x-3"
            >
              
              {/* Voice Button */}
              <button
                type="button"
                onClick={handleVoiceInput}
                disabled={isLoading}
                className={`p-3 rounded-xl transition-all duration-200 ${
                  isListening
                    ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/25'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-600/25'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={isListening ? 'Aufnahme stoppen' : 'Sprachnachricht aufnehmen'}
              >
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              
              {/* Text Input */}
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (!inFlightRef.current && inputValue.trim()) {
                        console.log("[ENTER-KEY] Direct handleSendMessage call");
                        handleSendMessage(inputValue.trim());
                      }
                    }
                  }}
                  placeholder="Fragen Sie Clara zu Ihren Immobilien..."
                  disabled={isLoading || isListening}
                  className="w-full px-4 py-3 bg-gray-800/60 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 backdrop-blur-sm transition-all duration-200"
                />
              </div>
              
              {/* Send Button */}
              <button
                type="submit"
                disabled={isLoading || !inputValue.trim() || isListening}
                className={`p-3 rounded-xl transition-all duration-200 ${
                  isLoading || !inputValue.trim() || isListening
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-600/25'
                }`}
                title="Nachricht senden"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* NEW: Floating 3D Upload Button */}
      <FloatingUploadButton3D 
        onClick={() => setUploadModalOpen(true)}
        position="bottom-right"
      />

      {/* NEW: Upload Document Modal */}
      <UploadDocumentModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onUpload={handleDocumentUpload}
      />

    </div>
  );
};
export default ClaraKIPage;

